import { assetFileShown } from "./asset-families";
import { issueSeverity, worded } from "./rule-catalog";
import { ruleImpl } from "./rule-impl";
import type {
  CardShape,
  CompositeMetricDefinition,
  LpComposite,
  MetricDefinition,
  MetricPlan,
  PriceRole,
  QuestionDefinition,
  QuestionnaireValidationIssue,
  ReportOptionPlan,
  ReportProjectionBindingName,
  ReportProjectionRequirement,
  ReportSourceRef,
  ReportingPlan,
  TestedAssetBlock,
} from "./index";
import type {
  DeclaredComparisonTable,
  DeclaredLiftBaseline,
  DeclaredPriceCurve,
  DeclaredQuadrantAxes,
  DeclaredReportAxis,
  ReportBindingOutcomes,
  ReportComparisonTable,
  ReportLiftBaseline,
  ReportPriceCurve,
  ReportQuadrantAxes,
  ReportingDeclaredBindings,
  ReportingProjectionSpecV2,
} from "./draft.generated";
import { readLiftTag } from "./lift-tags";
import { measureIdsFillingRole } from "./opportunity-role-tags";
import { openEndClassifierMetricId } from "./open-end-classifier";
import { PROFILE_TAG_PREFIX } from "./profiling";
import {
  ASSET_CHOICE_MIN_ASSETS,
  MATRIX_QUESTION_TYPES,
  RATING_DESIGN_QUESTION_TYPES,
} from "./question-capabilities";
import { RATING_UNIT_IDS, declaresLpTopics, lpTopicOfTags } from "./registry";
import { REPORT_CARD_SHAPE_CAPABILITIES } from "./report-card-capabilities";

const PRICE_CURVE_ROLES: Record<PriceRole, true> = {
  too_cheap: true,
  cheap: true,
  expensive: true,
  too_expensive: true,
};

const UNJOINED_SENTENCE =
  "This question was kept but its binding to the report was lost, so the card is not shown.";
const REPORTING_PROJECTION_V1 = "reporting-projection.v1";
const REPORTING_PROJECTION_V2 = "reporting-projection.v2";

// The one reason a declared binding with two feeders carries (`ambiguous`):
// the projection never picks one of them.
const AMBIGUOUS_BINDING_SENTENCE =
  "Two questions feed the read this card is declared on, so the card is not shown.";


type RecordValue = Record<string, unknown>;
type SourceIdentity = {
  moduleId: string;
  sourceKind: "recipe" | "reporting_target";
};

export type LiveReportCapabilityReason =
  | "available"
  | "no_report_source"
  | "insufficient_items"
  | "insufficient_compatible_per_item_metrics"
  | "declared_axes_unresolved"
  | "no_declared_axes"
  | "no_supported_verdict_composite";

export interface LiveReportSourceCapability {
  status: "available" | "unavailable";
  reason: LiveReportCapabilityReason;
  questionIds: string[];
  metricIds: string[];
  qualitativeAnalysisIds: string[];
}

/**
 * Which axes a positioning map plots, decided before any result (plots plan O).
 *
 * ONE rule with Python's `canonical_deck/quadrant_eligibility.py`
 * `quadrant_axis_method`; `tests/fixtures/plots/quadrant-eligibility.cases.json`
 * runs both. Declared axes (the stamp or the projection binding) win; otherwise
 * the recorded recipe's verdict composites (support on x, winner on y);
 * otherwise no map. Nothing picks two metrics by spread, name or id.
 */
export type QuadrantAxisMethod = "declared_axes" | "verdict_composites";

/** The recorded recipe's verdict, as `declaredVerdict` reads it. */
export interface QuadrantVerdictDeclaration {
  winnerCompositeId: string;
  supportCompositeId: string;
}

export function quadrantAxisMethod(
  plan:
    | {
        quadrantAxes?: unknown;
        projectionSpec?: {
          schemaVersion?: string;
          bindings?: { quadrantAxes?: unknown } | null;
        } | null;
      }
    | null
    | undefined,
  verdict: QuadrantVerdictDeclaration | null | undefined,
): QuadrantAxisMethod | null {
  const declared = plan?.projectionSpec?.bindings?.quadrantAxes != null;
  // Resolved axes, or a v1 build's stamp (its declaration was its resolution).
  if (
    plan?.quadrantAxes != null ||
    (declared &&
      (plan?.projectionSpec?.schemaVersion ?? REPORTING_PROJECTION_V1) ===
        REPORTING_PROJECTION_V1)
  ) {
    return "declared_axes";
  }
  // A v2 declaration the projection did not bind falls through to the
  // verdict, as an undeclared map does.
  if (verdict?.supportCompositeId && verdict?.winnerCompositeId) {
    return "verdict_composites";
  }
  return null;
}

export type LiveQuadrantCapability =
  | {
      status: "available";
      reason: "available";
      method: QuadrantAxisMethod;
      itemIds: string[];
      metricIds: string[];
      compositeMetricIds: string[];
    }
  | {
      status: "unavailable";
      reason:
        | "insufficient_items"
        | "no_declared_axes"
        | "no_supported_verdict_composite"
        | "declared_axes_unresolved";
      method: null;
      itemIds: string[];
      metricIds: string[];
      compositeMetricIds: string[];
    };

export type LivePodiumCapability =
  | {
      status: "available";
      reason: "available";
      itemIds: string[];
      compositeMetricId: string;
    }
  | {
      status: "unavailable";
      reason: "insufficient_items" | "no_supported_verdict_composite";
      itemIds: string[];
      compositeMetricId: null;
    };

/**
 * Pre-results mirror of `scorecard_facts.can_build_asset_scorecard` on its
 * per-asset means source: a family with 2+ assets and 2+ rating measures that
 * score them. A recipe that declares a deck layout owns its scorecard (the LP
 * layout's diagnostic scorecard), so it gets none here, as the deck's collector
 * refuses it (plan Stage 3, S3.4 row 21). No family is excluded by name.
 */
export type LiveScorecardCapability =
  | {
      status: "available";
      reason: "available";
      family: string;
      assetIds: Array<string>;
      metricIds: Array<string>;
    }
  | {
      status: "unavailable";
      reason:
        | "insufficient_items"
        | "insufficient_diagnostic_measures"
        | "declared_layout";
      family: null;
      assetIds: Array<string>;
      metricIds: [];
    };

/**
 * Pre-results mirror of the one thing a Van Westendorp deck slide needs before
 * any response exists: the four declared price roles still resolve to four
 * distinct, report-eligible questions of the source type the card capability
 * manifest declares for `price_curve`.
 *
 * Nothing here is a new rule. The binding is the one `projectReportingPlan`
 * already resolves (it returns `priceCurve: null` the moment a role question is
 * deleted or retyped away from `continuous`), and the cardinality and source
 * type are read off `REPORT_CARD_SHAPE_CAPABILITIES.price_curve`, the manifest
 * both languages load, rather than restated here. A declined binding therefore
 * declines this capability, and the planner then plans no PSM slide, without a
 * second validator anywhere.
 *
 * `referenceQuestionId` (D8) is deliberately NOT carried: the reference marker
 * needs the currency guard and the published median, neither of which exists
 * before results, so the producer stays its only reader.
 */
export type LivePriceCurveCapability =
  | {
      status: "available";
      reason: "available";
      questionIds: Array<string>;
      metricIds: Array<string>;
    }
  | {
      status: "unavailable";
      reason:
        | "binding_unresolved"
        | "unsupported_source_question_type"
        | "source_question_cardinality";
      questionIds: Array<string>;
      metricIds: [];
    };

/**
 * Pre-results mirror of the landing-page layout in the deck (Python
 * `report_layout`, plan Stage 3, S3.4 rows 21 and 30), which replaces the
 * per-asset rating evidence with three code-added exhibits: the
 * decision-scores composite matrix, the diagnostic scorecard and the
 * five-second readout.
 *
 * Only what is knowable before results exist lives here: the recorded recipe
 * declares the `landing_page` deck layout, the metric plan authored the
 * composites it is built on, and 2+ assets of the composites' family can be
 * scored on them (the deck's podium capability). No asset family is read. The deck still declines any exhibit its
 * results do not support, and `slide-plan/index.ts` resolves the scorecard's
 * own provenance (the rating questions/metrics the collapse strips) on top of
 * this capability.
 *
 * `comprehensionSourceIds` is the five-second readout's source, resolved the
 * way `scorecard_facts.asset_scores_for_topic` resolves it: the comprehension
 * composite when one exists, otherwise the raw rating metrics whose topic key
 * carries the topic. Empty means no five-second readout.
 */
export type LiveLandingPageExhibitsCapability =
  | {
      status: "available";
      reason: "available";
      assetIds: Array<string>;
      compositeMetricIds: Array<string>;
      comprehensionSourceIds: Array<string>;
    }
  | {
      status: "unavailable";
      reason:
        | "no_declared_layout"
        | "insufficient_items"
        | "no_composite_metrics";
      assetIds: Array<string>;
      compositeMetricIds: [];
      comprehensionSourceIds: [];
    };

/**
 * One clock-free answer to the question "what can this accepted study report?".
 *
 * Every input is a typed live artifact. In particular this projection never
 * reads titles, prose, stable-key fragments, recipe names, or tags other than
 * the explicit `verdict:pick` role stamped by assembly.
 */
export interface LiveReportCapabilities {
  reportQuestionIds: string[];
  quantitative: LiveReportSourceCapability;
  qualitative: LiveReportSourceCapability;
  /** Current custom report sources that deliberately do not impersonate a recipe input. */
  untargetedCustomQuantitative: LiveReportSourceCapability;
  untargetedCustomQualitative: LiveReportSourceCapability;
  quadrant: LiveQuadrantCapability;
  podium: LivePodiumCapability;
  scorecard: LiveScorecardCapability;
  landingPage: LiveLandingPageExhibitsCapability;
  priceCurve: LivePriceCurveCapability;
}

const record = (value: unknown): value is RecordValue =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const records = (value: unknown): RecordValue[] =>
  Array.isArray(value) ? value.filter(record) : [];

const reportSourceIdentity = (
  question: RecordValue,
): SourceIdentity | undefined => {
  if (!record(question.planAttribution)) return undefined;
  const recipe = question.planAttribution.recipeModuleId;
  const target = question.planAttribution.reportingTargetModuleId;
  if (typeof recipe === "string" && recipe.length > 0 && target == null) {
    return { moduleId: recipe, sourceKind: "recipe" };
  }
  if (typeof target === "string" && target.length > 0 && recipe == null) {
    return { moduleId: target, sourceKind: "reporting_target" };
  }
  return undefined;
};

const currentCustomReportInput = (
  question: RecordValue,
): string | undefined => {
  if (!record(question.planAttribution)) return undefined;
  const custom = question.planAttribution.customReporting;
  if (
    record(custom) &&
    custom.kind === "report" &&
    typeof custom.reportInputModuleId === "string" &&
    custom.reportInputModuleId.trim().length > 0
  ) {
    return custom.reportInputModuleId;
  }
  return undefined;
};

const reportSourceId = (question: RecordValue): string | undefined =>
  reportSourceIdentity(question)?.moduleId ??
  currentCustomReportInput(question);

const bindingReportInput = (binding: RecordValue): string | undefined => {
  if (
    validSourceRef(binding.sourceRef) &&
    binding.sourceRef.kind === "recipe_module"
  ) {
    return binding.sourceRef.moduleId;
  }
  if (
    validSourceRef(binding.sourceRef) &&
    binding.sourceRef.kind === "custom_topic"
  ) {
    return typeof binding.reportInputModuleId === "string"
      ? binding.reportInputModuleId
      : undefined;
  }
  return typeof binding.moduleId === "string" ? binding.moduleId : undefined;
};

const validSourceRef = (value: unknown): value is ReportSourceRef =>
  record(value) &&
  ((value.kind === "recipe_module" &&
    typeof value.moduleId === "string" &&
    value.moduleId.length > 0) ||
    (value.kind === "custom_topic" &&
      typeof value.topicKey === "string" &&
      value.topicKey.length > 0));

const validSourceBindingIdentity = (binding: RecordValue): boolean => {
  if (!validSourceRef(binding.sourceRef)) {
    return (
      binding.sourceRef == null &&
      typeof binding.moduleId === "string" &&
      binding.moduleId.length > 0 &&
      binding.reportInputModuleId == null &&
      (binding.sourceKind == null ||
        binding.sourceKind === "recipe" ||
        binding.sourceKind === "reporting_target")
    );
  }
  if (binding.sourceRef.kind === "custom_topic") {
    return (
      binding.moduleId == null &&
      binding.sourceKind == null &&
      (binding.reportInputModuleId == null ||
        (typeof binding.reportInputModuleId === "string" &&
          binding.reportInputModuleId.trim().length > 0))
    );
  }
  return (
    binding.reportInputModuleId == null &&
    (binding.moduleId == null ||
      binding.moduleId === binding.sourceRef.moduleId) &&
    (binding.sourceKind == null || binding.sourceKind === "recipe")
  );
};

const questionSourceRef = (
  question: RecordValue,
): ReportSourceRef | undefined => {
  if (!record(question.planAttribution)) return undefined;
  const attribution = question.planAttribution;
  if (
    typeof attribution.recipeModuleId === "string" &&
    attribution.recipeModuleId.length > 0
  ) {
    return { kind: "recipe_module", moduleId: attribution.recipeModuleId };
  }
  if (
    typeof attribution.topicKey === "string" &&
    attribution.topicKey.length > 0 &&
    record(attribution.customReporting) &&
    attribution.customReporting.kind === "report"
  ) {
    return { kind: "custom_topic", topicKey: attribution.topicKey };
  }
  return undefined;
};

const bindingMatchesQuestion = (
  binding: RecordValue,
  question: RecordValue,
): boolean => {
  if (!validSourceBindingIdentity(binding)) return false;
  if (validSourceRef(binding.sourceRef)) {
    if (
      stableJson(questionSourceRef(question)) !== stableJson(binding.sourceRef)
    ) {
      return false;
    }
    if (binding.sourceRef.kind === "custom_topic") {
      return (
        (typeof binding.reportInputModuleId === "string"
          ? binding.reportInputModuleId
          : undefined) === currentCustomReportInput(question)
      );
    }
    return binding.reportInputModuleId == null;
  }
  const legacy = reportSourceIdentity(question);
  return (
    legacy?.moduleId === binding.moduleId &&
    (binding.sourceKind == null || legacy?.sourceKind === binding.sourceKind)
  );
};

const validRequirement = (
  value: unknown,
): value is ReportProjectionRequirement => {
  if (!record(value)) return false;
  if (value.kind === "sources") return value.binding == null;
  return (
    value.kind === "declared_binding" &&
    ["quadrantAxes", "liftBaseline", "comparison", "priceCurve"].includes(
      value.binding as string,
    )
  );
};

/**
 * A projection stamp other than the current one. A plan with no stamp is not
 * this: that historical path stays with its own checks.
 */
function isRetiredReportingProjection(value: unknown): boolean {
  if (!record(value) || !record(value.projectionSpec)) return false;
  return value.projectionSpec.schemaVersion !== REPORTING_PROJECTION_V2;
}

/** Explicit versioning is the only opt-in. Old stamps are never reverse-engineered. */
function isProjectableReportingPlan(value: unknown): value is ReportingPlan {
  if (
    !record(value) ||
    !record(value.projectionSpec) ||
    value.projectionSpec.schemaVersion !== REPORTING_PROJECTION_V2 ||
    !record(value.projectionSpec.bindings) ||
    !Array.isArray(value.sourceBindings) ||
    !Array.isArray(value.cards) ||
    !record(value.slots)
  ) {
    return false;
  }
  const validModules = (candidate: unknown): candidate is string[] =>
    Array.isArray(candidate) &&
    candidate.length > 0 &&
    candidate.every((item) => typeof item === "string" && item.length > 0);
  const validQuestionIds = (candidate: unknown): candidate is string[] =>
    Array.isArray(candidate) &&
    candidate.length > 0 &&
    candidate.every((item) => typeof item === "string" && item.length > 0);
  const validCardDeclaration = (card: RecordValue): boolean =>
    (validModules(card.candidateModules) || validModules(card.fromModules)) &&
    ((typeof card.candidateWhenAbsent === "string" &&
      card.candidateWhenAbsent.length > 0) ||
      (typeof card.whenAbsent === "string" && card.whenAbsent.length > 0));
  const validOptionDeclaration = (option: RecordValue): boolean =>
    validModules(option.candidateModules) ||
    validModules(option.fromModules) ||
    (Array.isArray(option.fromModules) &&
      option.fromModules.length === 0 &&
      validQuestionIds(option.sourceQuestionIds));
  if (
    !value.sourceBindings.every(
      (binding) =>
        record(binding) &&
        typeof binding.stableKey === "string" &&
        typeof binding.questionId === "string" &&
        validSourceBindingIdentity(binding),
    )
  ) {
    return false;
  }
  if (
    !value.cards.every(
      (card) =>
        record(card) &&
        validCardDeclaration(card) &&
        validRequirement(card.requirement) &&
        (card.sourceQuestionIds == null ||
          validQuestionIds(card.sourceQuestionIds)),
    )
  ) {
    return false;
  }
  const slots = value.slots;
  return ["kpi", "plot", "quotes"].every((name) => {
    const slot = slots[name];
    return (
      record(slot) &&
      Array.isArray(slot.options) &&
      ((typeof slot.candidateWhenAbsent === "string" &&
        slot.candidateWhenAbsent.length > 0) ||
        (typeof slot.whenAbsent === "string" && slot.whenAbsent.length > 0)) &&
      slot.options.every(
        (option) =>
          record(option) &&
          validOptionDeclaration(option) &&
          (option.sourceQuestionIds == null ||
            validQuestionIds(option.sourceQuestionIds)) &&
          validRequirement(option.requirement),
      )
    );
  });
}

function stableJson(value: unknown): string {
  const normalize = (candidate: unknown): unknown => {
    if (Array.isArray(candidate)) return candidate.map(normalize);
    if (!record(candidate)) return candidate;
    return Object.fromEntries(
      Object.keys(candidate)
        .sort()
        .map((key) => [key, normalize(candidate[key])]),
    );
  };
  return JSON.stringify(normalize(value));
}

function reportingResolution(plan: ReportingPlan): unknown {
  return {
    bindingOutcomes: plan.bindingOutcomes,
    cards: plan.cards,
    slots: plan.slots,
    quadrantAxes: plan.quadrantAxes,
    liftBaseline: plan.liftBaseline,
    comparison: plan.comparison,
    priceCurve: plan.priceCurve,
  };
}

const sortedUnique = (values: Iterable<string>): string[] =>
  [...new Set(values)].sort((left, right) => left.localeCompare(right));

const optionSourceQuestions = (args: {
  option: ReportOptionPlan;
  questions: ReadonlyArray<QuestionDefinition>;
}): QuestionDefinition[] => {
  const explicit = new Set(args.option.sourceQuestionIds ?? []);
  if (explicit.size > 0) {
    return args.questions.filter((question) => explicit.has(question.id));
  }
  const modules = new Set(args.option.fromModules);
  return args.questions.filter((question) => {
    const source = reportSourceId(question as unknown as RecordValue);
    return source != null && modules.has(source);
  });
};

/**
 * The answer types a report-card option can draw: its shape's, narrowed by a
 * podium's basis, or widened by a KPI row's focal item to the grids whose
 * focal row it reads (a grid's focal row is a scalar).
 */
const optionSourceQuestionTypes = (
  option: ReportOptionPlan,
): ReadonlyArray<string> => {
  const capability = REPORT_CARD_SHAPE_CAPABILITIES[option.shape as CardShape];
  if (option.shape === "podium" && option.basis) {
    return capability.sourceQuestionTypesByBasis?.[option.basis] ?? [];
  }
  if (option.shape === "kpi_rows" && option.item != null) {
    return [
      ...capability.sourceQuestionTypes,
      ...capability.itemSourceQuestionTypes,
    ];
  }
  return capability.sourceQuestionTypes;
};

/**
 * Whether a question retyped to its current type can keep its report source
 * (its recipe module, or the module its custom report feeds).
 *
 * A binding stays joined through a type change: its identity is its stableKey
 * and sourceRef, and whether a type fits a card is the capability manifest's.
 * A card a module feeds does not filter its questions by type, so a planned
 * card this question feeds through its module, whose shape cannot draw the new
 * type, is lost or refused:
 * - a card that needs only its sources goes absent at projection. When other
 *   questions feed it too, keeping the source would take the card from them,
 *   so the question leaves its module instead; when it is the card's only
 *   source, the card goes either way and the question keeps its source;
 * - a card that needs a declared binding stays planned while that binding
 *   still resolves, and the write gate refuses it, so the question leaves its
 *   module.
 * A card that lists its questions (`sourceQuestionIds`) picks them by type at
 * projection, so a retype never costs it. `questions` holds the retyped
 * question; `plan` is the one stamped before the retype.
 */
export function retypeKeepsReportSource(args: {
  plan: ReportingPlan | null | undefined;
  questions: ReadonlyArray<QuestionDefinition>;
  question: QuestionDefinition;
  metricPlan?: MetricPlan | null;
  testedAssets?: TestedAssetBlock | null;
}): boolean {
  const { plan, questions, question } = args;
  const source = reportSourceId(question as unknown as RecordValue);
  if (!source || !isProjectableReportingPlan(plan)) return true;
  const options = (candidate: ReportingPlan): ReportOptionPlan[] => [
    ...(candidate.slots?.kpi.options ?? []),
    ...(candidate.slots?.plot.options ?? []),
    ...(candidate.slots?.quotes.options ?? []),
  ];
  const lost = (option: ReportOptionPlan): boolean =>
    option.status === "planned" &&
    (option.sourceQuestionIds ?? []).length === 0 &&
    option.fromModules.includes(source) &&
    !optionSourceQuestionTypes(option).includes(question.type);
  const takesSharedCard = options(plan).some(
    (option) =>
      lost(option) &&
      option.requirement?.kind === "sources" &&
      optionSourceQuestions({ option, questions }).some(
        (other) => other.id !== question.id,
      ),
  );
  if (takesSharedCard) return false;
  const projected = projectReportingPlan({
    plan,
    questions,
    metricPlan: args.metricPlan,
    testedAssets: args.testedAssets,
  });
  return !projected || !options(projected).some(lost);
}

const questionItemIds = (
  question: QuestionDefinition,
  testedAssets: TestedAssetBlock | null | undefined,
): string[] => {
  if (
    (question.type === "matrix_rating" &&
      question.response.kind === "matrix_rating") ||
    (question.type === "matrix_categorical" &&
      question.response.kind === "matrix_categorical")
  ) {
    const rows = question.response.rows;
    if (rows.source === "fixed") {
      return sortedUnique(rows.rows.map((row) => row.value || row.id));
    }
    return testedAssetIdsForTypes(testedAssets, [rows.assetType]);
  }
  if (question.assetBinding?.mode === "per_asset") {
    return testedAssetIdsForTypes(
      testedAssets,
      question.assetBinding.assetTypes,
    );
  }
  if (
    (question.type === "single_select" &&
      question.response.kind === "single_select") ||
    (question.type === "multi_select" &&
      question.response.kind === "multi_select") ||
    (question.type === "ranking" && question.response.kind === "ranking")
  ) {
    const referencedAssets = sortedUnique(
      question.response.options.flatMap((option) =>
        (option.assetRefs ?? []).map((reference) => reference.assetId),
      ),
    );
    if (referencedAssets.length > 0) return referencedAssets;
    return sortedUnique(
      question.response.options
        .filter((option) => option.exclusive !== true)
        .map((option) => option.value || option.id),
    );
  }
  return [];
};

type ReportCardSlotName = "kpi" | "plot" | "quotes";
type ReportCardCapabilityMismatchKind =
  | "declaration"
  | "missing_source"
  | "presentation";
type ReportCardPresentationMismatchKind =
  | "source_cardinality"
  | "source_type"
  | "item_cardinality"
  | "asset_identity"
  | "media";

interface ReportCardCapabilityMismatch {
  kind: ReportCardCapabilityMismatchKind;
  presentationKind?: ReportCardPresentationMismatchKind;
  issue: QuestionnaireValidationIssue;
}

function reportCardOptionCapabilityMismatches(args: {
  slotName: ReportCardSlotName;
  option: ReportOptionPlan;
  optionIndex: number;
  questions: ReadonlyArray<QuestionDefinition>;
  testedAssets?: TestedAssetBlock | null;
}): ReportCardCapabilityMismatch[] {
  const { slotName, option, optionIndex } = args;
  const mismatches: ReportCardCapabilityMismatch[] = [];
  const fail = (
    kind: ReportCardCapabilityMismatchKind,
    path: string,
    message: string,
    presentationKind?: ReportCardPresentationMismatchKind,
  ) =>
    mismatches.push({
      kind,
      ...(presentationKind ? { presentationKind } : {}),
      issue: {
        path,
        code: "reporting_card_capability_invalid",
        severity: issueSeverity("reporting_card_capability_invalid"),
        message: worded("reporting_card_capability_invalid", message),
      },
    });
  const path = `reportingPlan.slots.${slotName}.options[${optionIndex}]`;
  const capability = REPORT_CARD_SHAPE_CAPABILITIES[option.shape as CardShape];
  if (capability.status !== "current_generated") {
    fail(
      "declaration",
      `${path}.shape`,
      `Report-card shape ${option.shape} is ${capability.status} and cannot be written by the current questionnaire builder.`,
    );
  }
  if (!capability.slots.includes(slotName)) {
    fail(
      "declaration",
      `${path}.shape`,
      `Report-card shape ${option.shape} is not legal in the ${slotName} slot.`,
    );
  }

  const hasBasis = option.basis != null;
  if (
    (capability.basis === "required" && !hasBasis) ||
    (capability.basis === "forbidden" && hasBasis)
  ) {
    fail(
      "declaration",
      `${path}.basis`,
      `Report-card shape ${option.shape} has an invalid basis field.`,
    );
  }
  const hasItem = option.item != null;
  if (
    (capability.item === "required" && !hasItem) ||
    (capability.item === "forbidden" && hasItem)
  ) {
    fail(
      "declaration",
      `${path}.item`,
      `Report-card shape ${option.shape} has an invalid item field.`,
    );
  }
  if (
    stableJson(option.requirement) !== stableJson(capability.requirement)
  ) {
    fail(
      "declaration",
      `${path}.requirement`,
      `Report-card shape ${option.shape} does not carry its declared prerequisite.`,
    );
  }
  if (option.status !== "planned") return mismatches;

  const sourceQuestions = optionSourceQuestions({
    option,
    questions: args.questions,
  });
  const sourceCount = sourceQuestions.length;
  const cardinality = capability.sourceQuestionCardinality;
  if (
    sourceCount < cardinality.min ||
    (cardinality.max != null && sourceCount > cardinality.max)
  ) {
    fail(
      sourceCount === 0 ? "missing_source" : "presentation",
      path,
      `Report-card shape ${option.shape} has ${sourceCount} live source questions; expected ${cardinality.min}${
        cardinality.max == null ? "+" : `-${cardinality.max}`
      }.`,
      sourceCount === 0 ? undefined : "source_cardinality",
    );
  }

  const allowedTypes = new Set(optionSourceQuestionTypes(option));
  sourceQuestions.forEach((question) => {
    if (!allowedTypes.has(question.type)) {
      fail(
        "presentation",
        path,
        `Question ${question.id} of type ${question.type} cannot feed report-card shape ${option.shape}.`,
        "source_type",
      );
    }
  });

  if (capability.minimumItemCardinality >= 2) {
    const itemIds = sortedUnique(
      sourceQuestions.flatMap((question) =>
        questionItemIds(question, args.testedAssets),
      ),
    );
    if (itemIds.length < capability.minimumItemCardinality) {
      fail(
        "presentation",
        path,
        `Report-card shape ${option.shape} has ${itemIds.length} typed items; expected at least ${capability.minimumItemCardinality}.`,
        "item_cardinality",
      );
    }
  }
  // This display policy is introduced with current evidence provenance. Saved
  // sources without it keep their frozen interpretation on historical reads.
  const currentDisplayProjection =
    sourceQuestions.length > 0 &&
    sourceQuestions.every(
      (question) => question.planAttribution?.evidenceRequirementIds != null,
    );
  if (
    currentDisplayProjection &&
    option.basis &&
    capability.assetChoicesRequiredByBasis?.includes(option.basis)
  ) {
    for (const question of sourceQuestions) {
      const response = question.response;
      if (
        response.kind !== "single_select" &&
        response.kind !== "multi_select" &&
        response.kind !== "ranking"
      )
        continue;
      if (
        response.options.some(
          (choice) =>
            choice.exclusive !== true &&
            !(choice.assetRefs ?? []).some((ref) => ref.role === "choice"),
        )
      ) {
        fail(
          "presentation",
          path,
          "An asset podium requires exact asset identities on its answer choices.",
          "asset_identity",
        );
      }
    }
  }
  if (
    currentDisplayProjection &&
    option.basis &&
    capability.mediaRequiredByBasis?.includes(option.basis)
  ) {
    const items = sourceQuestions.flatMap((question) =>
      questionItemIds(question, args.testedAssets),
    );
    const assets = new Map(
      (args.testedAssets?.assets ?? []).map((asset) => [asset.id, asset]),
    );
    if (items.some((id) => !assetFileShown(assets.get(id) ?? {}))) {
      fail(
        "presentation",
        path,
        `Report-card shape ${option.shape} requires media for its picture display.`,
        "media",
      );
    }
  }
  return mismatches;
}

function reportCardShapeCapabilityIssues(args: {
  plan: ReportingPlan;
  questions: ReadonlyArray<QuestionDefinition>;
  testedAssets?: TestedAssetBlock | null;
}): QuestionnaireValidationIssue[] {
  const issues: QuestionnaireValidationIssue[] = [];
  const slots = args.plan.slots;
  if (!slots) return issues;

  (["kpi", "plot", "quotes"] as const).forEach((slotName) => {
    const options = slots[slotName]?.options;
    if (!options) return;
    options.forEach((option, optionIndex) => {
      issues.push(
        ...reportCardOptionCapabilityMismatches({
          slotName,
          option,
          optionIndex,
          questions: args.questions,
          testedAssets: args.testedAssets,
        }).map(({ issue }) => issue),
      );
    });
  });
  return issues;
}

/**
 * A question reported in Survey Data only: it has no report read, so the
 * slide builder gives it no slide and the validators require none.
 */
export function isSurveyDataOnlyQuestion(
  question: Pick<QuestionDefinition, "planAttribution">,
): boolean {
  const attribution = question.planAttribution;
  return Boolean(
    attribution?.surveyDataOnlyReason ||
    attribution?.customReporting?.kind === "survey_data_only",
  );
}

function liveReportSourceState(args: {
  plan: ReportingPlan | null | undefined;
  questions: ReadonlyArray<QuestionDefinition>;
}): {
  reportQuestionIds: Set<string>;
  untargetedCustomQuestionIds: Set<string>;
} {
  const { plan, questions } = args;
  const reportQuestionIds = new Set<string>();
  const untargetedCustomQuestionIds = new Set<string>();
  const bindings = Array.isArray(plan?.sourceBindings)
    ? plan.sourceBindings
    : null;

  // A frozen source list is authoritative when present. A missing or
  // rerouted binding is not replaced by a nearby question that happens to
  // look useful.
  if (bindings) {
    const byStableKey = new Map(
      questions.map((question) => [question.stableKey, question]),
    );
    for (const binding of bindings) {
      const question = byStableKey.get(binding.stableKey);
      if (
        !question ||
        !bindingMatchesQuestion(
          binding as unknown as RecordValue,
          question as unknown as RecordValue,
        )
      ) {
        continue;
      }
      reportQuestionIds.add(question.id);
      if (
        validSourceRef(binding.sourceRef) &&
        binding.sourceRef.kind === "custom_topic" &&
        bindingReportInput(binding as unknown as RecordValue) == null
      ) {
        untargetedCustomQuestionIds.add(question.id);
      }
    }
    return { reportQuestionIds, untargetedCustomQuestionIds };
  }

  // Planless/manual and historical drafts keep their established inference
  // path. Only the explicit Survey Data-only disposition removes a question.
  for (const question of questions) {
    if (!isSurveyDataOnlyQuestion(question)) reportQuestionIds.add(question.id);
  }
  return { reportQuestionIds, untargetedCustomQuestionIds };
}

function sourceCapability(args: {
  questionIds: ReadonlySet<string>;
  metricPlan: MetricPlan | null | undefined;
}): {
  quantitative: LiveReportSourceCapability;
  qualitative: LiveReportSourceCapability;
} {
  const metricIds = sortedUnique(
    (args.metricPlan?.metrics ?? [])
      .filter((metric) => args.questionIds.has(metric.questionId))
      .map((metric) => metric.id),
  );
  const qualitativeAnalyses = (
    args.metricPlan?.qualitativeAnalyses ?? []
  ).filter((analysis) => args.questionIds.has(analysis.questionId));
  const qualitativeIds = sortedUnique(
    qualitativeAnalyses.map((analysis) => analysis.id),
  );
  const quantitativeQuestions = sortedUnique(
    (args.metricPlan?.metrics ?? [])
      .filter((metric) => args.questionIds.has(metric.questionId))
      .map((metric) => metric.questionId),
  );
  const qualitativeQuestions = sortedUnique(
    qualitativeAnalyses.map((analysis) => analysis.questionId),
  );
  return {
    quantitative: {
      status: metricIds.length > 0 ? "available" : "unavailable",
      reason: metricIds.length > 0 ? "available" : "no_report_source",
      questionIds: quantitativeQuestions,
      metricIds,
      qualitativeAnalysisIds: [],
    },
    qualitative: {
      status: qualitativeIds.length > 0 ? "available" : "unavailable",
      reason: qualitativeIds.length > 0 ? "available" : "no_report_source",
      questionIds: qualitativeQuestions,
      metricIds: [],
      qualitativeAnalysisIds: qualitativeIds,
    },
  };
}

const testedAssetIdsForTypes = (
  testedAssets: TestedAssetBlock | null | undefined,
  assetTypes: ReadonlyArray<string>,
): string[] => {
  const allowed = new Set(assetTypes);
  return sortedUnique(
    (testedAssets?.assets ?? [])
      .filter((asset) => allowed.has(asset.type))
      .map((asset) => asset.id),
  );
};

/**
 * The first tested-asset family (authored order of first appearance) that
 * carries 2+ distinct asset ids: a scorecard compares assets within one
 * family, so a family of one has nothing to compare.
 */
function firstQualifyingScorecardFamily(
  testedAssets: TestedAssetBlock | null | undefined,
): string | null {
  const order: string[] = [];
  const idsByFamily = new Map<string, Set<string>>();
  for (const asset of testedAssets?.assets ?? []) {
    if (!idsByFamily.has(asset.type)) {
      idsByFamily.set(asset.type, new Set());
      order.push(asset.type);
    }
    idsByFamily.get(asset.type)!.add(asset.id);
  }
  return order.find((family) => (idsByFamily.get(family)?.size ?? 0) >= 2) ?? null;
}

/**
 * The landing-page topic of the five-second readout, as
 * `scorecard_facts.LP_COMPREHENSION_TOPIC` names it: a registered topic
 * (`LpComposite`), read off each question's `lp:` tag.
 */
const LP_COMPREHENSION_TOPIC: LpComposite = "comprehension";

/**
 * LEGACY READERS ONLY, for a study with no registered `lp:` tag at all (saved
 * before the tags were stamped). Mirror `scorecard_facts.legacy_metric_topic_key`
 * and `_legacy_composite_matches_topic`; a tagged study never reaches them.
 */
const LEGACY_METRIC_PREFIX_RE = /^metric_/i;
const LEGACY_METRIC_ASSET_INFIX_RE = /^(?:lp|asset)[_-]?\d+[_-]/i;
const LEGACY_COMPREHENSION_SUBSTRING = "comprehension";

function legacyMetricTopicKey(metricId: string): string {
  const stripped = (metricId ?? "")
    .replace(LEGACY_METRIC_PREFIX_RE, "")
    .replace(LEGACY_METRIC_ASSET_INFIX_RE, "");
  return stripped || metricId;
}

function uniqueInOrder(values: Iterable<string>): Array<string> {
  const seen = new Set<string>();
  const result: Array<string> = [];
  for (const value of values) {
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    result.push(value);
  }
  return result;
}

function matrixRowItemIds(question: QuestionDefinition): string[] {
  if (
    question.type !== "matrix_rating" ||
    question.response.kind !== "matrix_rating"
  )
    return [];
  const rows = question.response.rows;
  if (rows.source !== "fixed") return [];
  return sortedUnique(rows.rows.map((row) => row.value || row.id));
}

/** The item keys the current canonical analysis writes for this metric. */
function perItemMetricIds(args: {
  metric: MetricDefinition;
  question: QuestionDefinition;
  testedAssets: TestedAssetBlock | null | undefined;
  allowFixedRows?: boolean;
}): string[] {
  const { metric, question, testedAssets } = args;
  const source = metric.source;
  if (!source) return [];
  if (source.kind === "asset_measure") {
    return testedAssetIdsForTypes(testedAssets, [source.assetType]);
  }
  if (
    question.type === "matrix_rating" &&
    question.response.kind === "matrix_rating" &&
    source.kind === "matrix_measure" &&
    source.questionId === question.id
  ) {
    const rows = question.response.rows;
    if (rows.source === "tested_assets") {
      return testedAssetIdsForTypes(testedAssets, [rows.assetType]);
    }
    return args.allowFixedRows ? matrixRowItemIds(question) : [];
  }
  if (question.assetBinding?.mode === "per_asset") {
    return testedAssetIdsForTypes(
      testedAssets,
      question.assetBinding.assetTypes,
    );
  }
  if (
    question.type === "single_select" &&
    question.response.kind === "single_select" &&
    metric.extraction.method === "distribution" &&
    question.tags.includes("verdict:pick")
  ) {
    const testedIds = new Set(
      (testedAssets?.assets ?? []).map((asset) => asset.id),
    );
    return sortedUnique(
      question.response.options.flatMap((option) =>
        (option.assetRefs ?? [])
          .map((ref) => ref.assetId)
          .filter((assetId) => testedIds.has(assetId)),
      ),
    );
  }
  return [];
}

function metricIdForAxis(
  axis: { questionId: string; measureId?: string | null },
  metrics: ReadonlyArray<MetricDefinition>,
): string | null {
  const matches = metrics.filter((metric) => {
    if (metric.questionId !== axis.questionId) return false;
    if (!metric.source) return false;
    if (axis.measureId == null) return metric.source.kind === "question";
    return (
      metric.source.kind === "matrix_measure" &&
      metric.source.measureId === axis.measureId
    );
  });
  return matches.length === 1 ? matches[0].id : null;
}

/**
 * What a planned composite's liveness reads off its draft: which metrics are
 * live, the draft's questions and its tested assets.
 */
export interface CompositeLiveness {
  metricIsLive: (metricId: string) => boolean;
  questions: ReadonlyArray<unknown>;
  testedAssets?: TestedAssetBlock | null;
}

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * How many entries a podium may crown on this primary, or null (decision 19).
 *
 * 1 for a single pick, a ranking, a grid over the items or a lane rating; a
 * bounded pick's stamped `maxSelected` when it is at least 1; null for
 * anything else, an unbounded pick included (it forces no choice). Python's
 * `study_verdict.podium_crown_size` mirrors it; the shared fixture
 * `tests/fixtures/reporting/podium-crown-size.v1.json` keeps the two in step.
 */
export function podiumCrownSize(
  question: Pick<QuestionDefinition, "type" | "response">,
): number | null {
  switch (question.type) {
    case "single_select":
    case "ranking":
    case "matrix_rating":
    case "rating":
      return 1;
    case "multi_select": {
      const bound =
        question.response?.kind === "multi_select"
          ? question.response.maxSelected
          : undefined;
      return typeof bound === "number" && Number.isInteger(bound) && bound >= 1
        ? bound
        : null;
    }
    default:
      return null;
  }
}

/**
 * A choice question whose contenders (its non-exclusive options) each name
 * one of `itemIds` through an exact `choice` asset ref, and together name all
 * of them: the option join a podium ranks a pick through.
 */
function choicesNameAllItems(
  question: QuestionDefinition,
  itemIds: ReadonlyArray<string>,
): boolean {
  const response = question.response;
  if (
    response?.kind !== "single_select" &&
    response?.kind !== "multi_select" &&
    response?.kind !== "ranking"
  ) {
    return false;
  }
  const wanted = new Set(itemIds);
  const named = new Set<string>();
  for (const option of response.options) {
    if (option.exclusive === true) continue;
    const reference = (option.assetRefs ?? []).find(
      (candidate) =>
        candidate.role === "choice" && wanted.has(candidate.assetId),
    );
    if (!reference) return false;
    named.add(reference.assetId);
  }
  return named.size === wanted.size;
}

/**
 * The one podium rule (decisions 6, 19, 22): the study's declared primary
 * gives every tested item of `appliesTo` one value, so the podium ranks it.
 *
 * Typed facts only, never wording, a topic role or `verdict:pick`:
 * - its type is one the podium's `chosen` basis draws
 *   (`podium.sourceQuestionTypesByBasis.chosen`, the one list);
 * - a grid: rows drawn from the tested items of `appliesTo`, one measure;
 * - a lane rating: asked once per tested item of `appliesTo`;
 * - a single pick or a ranking: its options name exactly the tested items;
 * - a bounded pick: the same, with a bound below the item count;
 * - always at least `ASSET_CHOICE_MIN_ASSETS` tested items.
 * `crownSize` is `podiumCrownSize`.
 */
export function primaryRanksTestedItems(
  question: QuestionDefinition,
  testedAssets: TestedAssetBlock | null | undefined,
  appliesTo: string,
): { ranks: boolean; crownSize: number | null } {
  const crownSize = podiumCrownSize(question);
  const itemIds = testedAssetIdsForTypes(testedAssets, [appliesTo]);
  const chosenTypes: ReadonlyArray<string> =
    REPORT_CARD_SHAPE_CAPABILITIES.podium.sourceQuestionTypesByBasis?.chosen ??
    [];
  if (
    crownSize == null ||
    !chosenTypes.includes(question.type) ||
    itemIds.length < ASSET_CHOICE_MIN_ASSETS
  ) {
    return { ranks: false, crownSize };
  }
  const response = question.response;
  let ranks = false;
  if (question.type === "matrix_rating") {
    ranks =
      response?.kind === "matrix_rating" &&
      response.rows.source === "tested_assets" &&
      response.rows.assetType === appliesTo &&
      response.measures.length === 1;
  } else if (question.type === "rating") {
    const binding = question.assetBinding;
    ranks =
      response?.kind === "rating" &&
      binding?.mode === "per_asset" &&
      binding.assetTypes.includes(
        appliesTo as (typeof binding.assetTypes)[number],
      );
  } else if (question.type === "multi_select") {
    ranks = choicesNameAllItems(question, itemIds) && crownSize < itemIds.length;
  } else {
    ranks = choicesNameAllItems(question, itemIds);
  }
  return { ranks, crownSize };
}

/**
 * The one liveness rule for a planned composite (C15a). Slide planning, the
 * podium capability and launch-snapshot pruning all read it, so a podium is
 * never planned on a composite the launch then drops, or dropped from one it
 * planned.
 *
 * - `winner_composite`: at least one component, and every component live.
 * - `study_verdict` (the podium slot ranked by the study verdict): the draft
 *   declares exactly one primary question (`plan:primary`, the tag Python's
 *   `PLAN_PRIMARY_TAG` owns) and that primary ranks the tested items of
 *   `appliesTo` (`primaryRanksTestedItems`). A Save that prunes the primary,
 *   or turns it into a read that ranks nothing, drops the marker and its
 *   podium together.
 */
export function compositeIsLive(
  composite: unknown,
  draft: CompositeLiveness,
): boolean {
  if (!isPlainRecord(composite)) return false;
  if (composite.kind === "study_verdict") {
    const primaries = draft.questions.filter(
      (question) =>
        isPlainRecord(question) &&
        Array.isArray(question.tags) &&
        question.tags.includes("plan:primary"),
    );
    const appliesTo =
      typeof composite.appliesTo === "string" ? composite.appliesTo : "";
    const primary = primaries[0];
    return (
      primaries.length === 1 &&
      isPlainRecord(primary) &&
      isPlainRecord(primary.response) &&
      primaryRanksTestedItems(
        primary as unknown as QuestionDefinition,
        draft.testedAssets,
        appliesTo,
      ).ranks
    );
  }
  const components = Array.isArray(composite.components)
    ? composite.components
    : [];
  return (
    components.length > 0 &&
    components.every(
      (component) =>
        isPlainRecord(component) &&
        typeof component.metricId === "string" &&
        draft.metricIsLive(component.metricId),
    )
  );
}

function liveComposite(
  composites: ReadonlyArray<CompositeMetricDefinition>,
  id: string,
  liveness: CompositeLiveness,
): CompositeMetricDefinition | undefined {
  return composites.find(
    (composite) => composite.id === id && compositeIsLive(composite, liveness),
  );
}

/** A scored composite's components; the study-verdict marker has none. */
const componentsOf = (
  composite: CompositeMetricDefinition | undefined,
): ReadonlyArray<{ metricId: string }> =>
  composite?.kind === "winner_composite" ? composite.components : [];

/**
 * Derive the current report and slide capabilities from exact accepted sources.
 * This is the shared owner used by normalization, slide planning, and the
 * accepted-state verifier.
 */
export function deriveLiveReportCapabilities(args: {
  plan?: ReportingPlan | null;
  questions: ReadonlyArray<QuestionDefinition>;
  metricPlan?: MetricPlan | null;
  testedAssets?: TestedAssetBlock | null;
  /** The deck layout the recorded recipe declares (`declaredDeckLayout`). */
  deckLayout?: "landing_page" | null;
  /** The verdict the recorded recipe declares (`declaredVerdict`): the map's composites when no axes are declared. */
  verdict?: QuadrantVerdictDeclaration | null;
}): LiveReportCapabilities {
  const { plan, questions, metricPlan, testedAssets } = args;
  const deckLayout = args.deckLayout ?? null;
  const sourceState = liveReportSourceState({ plan, questions });
  const live = sourceCapability({
    questionIds: sourceState.reportQuestionIds,
    metricPlan,
  });
  const untargeted = sourceCapability({
    questionIds: sourceState.untargetedCustomQuestionIds,
    metricPlan,
  });
  const questionsById = new Map(
    questions.map((question) => [question.id, question]),
  );
  const metrics = (metricPlan?.metrics ?? []).filter((metric) =>
    sourceState.reportQuestionIds.has(metric.questionId),
  );
  const syntheticClassifierMetricIds = (metricPlan?.qualitativeAnalyses ?? [])
    .filter((analysis) =>
      sourceState.reportQuestionIds.has(analysis.questionId),
    )
    .flatMap((analysis) =>
      analysis.classifier
        ? [
            openEndClassifierMetricId(
              analysis.stableKey,
              analysis.classifier.metricKind,
            ),
          ]
        : [],
    );
  const liveMetricIds = new Set([
    ...metrics.map((metric) => metric.id),
    ...syntheticClassifierMetricIds,
  ]);
  const liveness: CompositeLiveness = {
    metricIsLive: (metricId) => liveMetricIds.has(metricId),
    questions,
    testedAssets,
  };
  const composites = metricPlan?.compositeMetrics ?? [];
  const allTestedItemIds = sortedUnique(
    (testedAssets?.assets ?? []).map((asset) => asset.id),
  );
  const itemIdsForMetric = (metricId: string): string[] => {
    const metric = metrics.find((candidate) => candidate.id === metricId);
    if (metric) {
      const question = questionsById.get(metric.questionId);
      return question
        ? perItemMetricIds({ metric, question, testedAssets })
        : [];
    }
    const analysis = (metricPlan?.qualitativeAnalyses ?? []).find(
      (candidate) =>
        candidate.classifier != null &&
        openEndClassifierMetricId(
          candidate.stableKey,
          candidate.classifier.metricKind,
        ) ===
          metricId,
    );
    const question = analysis
      ? questionsById.get(analysis.questionId)
      : undefined;
    return question?.assetBinding?.mode === "per_asset"
      ? testedAssetIdsForTypes(testedAssets, question.assetBinding.assetTypes)
      : [];
  };
  const compositeSupportsItems = (
    composite: CompositeMetricDefinition | undefined,
    itemIds: ReadonlyArray<string>,
  ): composite is CompositeMetricDefinition =>
    composite != null &&
    itemIds.length >= 2 &&
    componentsOf(composite).every(
      (component) =>
        stableJson(itemIdsForMetric(component.metricId)) ===
        stableJson(itemIds),
    );

  // One manifest governs both declared and inferred axes; a choice can identify
  // items but cannot become a coordinate merely because it covers those items.
  const quadrantSourceQuestionTypes: ReadonlySet<string> = new Set(
    REPORT_CARD_SHAPE_CAPABILITIES.quadrant.sourceQuestionTypes,
  );
  const isQuadrantSourceMetric = (
    metric: MetricDefinition | undefined,
  ): metric is MetricDefinition => {
    const question = metric ? questionsById.get(metric.questionId) : undefined;
    return (
      metric != null &&
      question != null &&
      quadrantSourceQuestionTypes.has(question.type)
    );
  };

  const quadrantMethod = quadrantAxisMethod(plan, args.verdict ?? null);
  let quadrant: LiveQuadrantCapability;
  if (quadrantMethod === "declared_axes") {
    const resolvedAxes = plan?.quadrantAxes;
    const xMetricId = resolvedAxes
      ? metricIdForAxis(resolvedAxes.x, metrics)
      : null;
    const yMetricId = resolvedAxes
      ? metricIdForAxis(resolvedAxes.y, metrics)
      : null;
    const xMetric = metrics.find((metric) => metric.id === xMetricId);
    const yMetric = metrics.find((metric) => metric.id === yMetricId);
    const xItems =
      xMetric && questionsById.get(xMetric.questionId)
        ? perItemMetricIds({
            metric: xMetric,
            question: questionsById.get(xMetric.questionId)!,
            testedAssets,
            allowFixedRows: true,
          })
        : [];
    const yItems =
      yMetric && questionsById.get(yMetric.questionId)
        ? perItemMetricIds({
            metric: yMetric,
            question: questionsById.get(yMetric.questionId)!,
            testedAssets,
            allowFixedRows: true,
          })
        : [];
    const sameItems =
      xItems.length >= 2 && stableJson(xItems) === stableJson(yItems);
    quadrant =
      xMetricId &&
      yMetricId &&
      isQuadrantSourceMetric(xMetric) &&
      isQuadrantSourceMetric(yMetric) &&
      xMetricId !== yMetricId &&
      sameItems
        ? {
            status: "available",
            reason: "available",
            method: "declared_axes",
            itemIds: xItems,
            metricIds: [xMetricId, yMetricId],
            compositeMetricIds: [],
          }
        : {
            status: "unavailable",
            reason: "declared_axes_unresolved",
            method: null,
            itemIds: sortedUnique([...xItems, ...yItems]),
            metricIds: sortedUnique(
              [xMetricId, yMetricId].filter(
                (value): value is string => typeof value === "string",
              ),
            ),
            compositeMetricIds: [],
          };
  } else if (quadrantMethod === "verdict_composites" && args.verdict) {
    // The recorded recipe's verdict composites: support on x, winner on y,
    // over the items both composites read (Python `_try_verdict_composite_quadrant_axes`).
    const support = liveComposite(
      composites,
      args.verdict.supportCompositeId,
      liveness,
    );
    const winner = liveComposite(
      composites,
      args.verdict.winnerCompositeId,
      liveness,
    );
    const firstComponent = componentsOf(support)[0]?.metricId;
    const verdictItemIds = firstComponent
      ? sortedUnique(itemIdsForMetric(firstComponent))
      : [];
    quadrant =
      compositeSupportsItems(support, verdictItemIds) &&
      compositeSupportsItems(winner, verdictItemIds) &&
      support.id !== winner.id
        ? {
            status: "available",
            reason: "available",
            method: "verdict_composites",
            itemIds: verdictItemIds,
            metricIds: [],
            compositeMetricIds: [support.id, winner.id],
          }
        : {
            status: "unavailable",
            reason:
              allTestedItemIds.length <
              REPORT_CARD_SHAPE_CAPABILITIES.quadrant.minimumItemCardinality
                ? "insufficient_items"
                : "no_supported_verdict_composite",
            method: null,
            itemIds: verdictItemIds,
            metricIds: [],
            compositeMetricIds: [],
          };
  } else {
    // Undeclared axes draw no map: two metrics are never picked for one.
    quadrant = {
      status: "unavailable",
      reason:
        allTestedItemIds.length <
        REPORT_CARD_SHAPE_CAPABILITIES.quadrant.minimumItemCardinality
          ? "insufficient_items"
          : "no_declared_axes",
      method: null,
      itemIds: allTestedItemIds,
      metricIds: [],
      compositeMetricIds: [],
    };
  }

  // The podium ranks the study's primary, only through the live study-verdict
  // slot (Python `report_layout.podium_composite_id` applies the same rule).
  const supportedPodiumComposite = composites.find(
    (candidate) =>
      candidate.kind === "study_verdict" && compositeIsLive(candidate, liveness),
  );
  const podiumItemIds = supportedPodiumComposite
    ? testedAssetIdsForTypes(testedAssets, [supportedPodiumComposite.appliesTo])
    : allTestedItemIds;
  const podium: LivePodiumCapability =
    supportedPodiumComposite && podiumItemIds.length >= 2
      ? {
          status: "available",
          reason: "available",
          itemIds: podiumItemIds,
          compositeMetricId: supportedPodiumComposite.id,
        }
      : {
          status: "unavailable",
          reason:
            supportedPodiumComposite && podiumItemIds.length < 2
              ? "insufficient_items"
              : "no_supported_verdict_composite",
          itemIds: podiumItemIds,
          compositeMetricId: null,
        };

  const scorecardFamily = firstQualifyingScorecardFamily(testedAssets);
  let scorecard: LiveScorecardCapability;
  if (deckLayout !== null) {
    scorecard = {
      status: "unavailable",
      reason: "declared_layout",
      family: null,
      assetIds: [],
      metricIds: [],
    };
  } else if (!scorecardFamily) {
    scorecard = {
      status: "unavailable",
      reason: "insufficient_items",
      family: null,
      assetIds: [],
      metricIds: [],
    };
  } else {
    const familyAssetIds = new Set(
      testedAssetIdsForTypes(testedAssets, [scorecardFamily]),
    );
    const diagnosticMetricIds = sortedUnique(
      metrics
        .filter((metric) => {
          const question = questionsById.get(metric.questionId);
          if (!question) {
            return false;
          }
          if (!RATING_DESIGN_QUESTION_TYPES.has(question.type)) {
            return false;
          }
          const hasUnresolvedMention = (question.assetRefs ?? []).some(
            (ref) => ref.source === "mention" && ref.mentionResolved !== true,
          );
          if (hasUnresolvedMention) {
            return false;
          }
          const items = perItemMetricIds({ metric, question, testedAssets });
          const covered = items.filter((id) => familyAssetIds.has(id)).length;
          return covered >= 2;
        })
        .map((metric) => metric.id),
    );
    scorecard =
      diagnosticMetricIds.length >= 2
        ? {
            status: "available",
            reason: "available",
            family: scorecardFamily,
            assetIds: sortedUnique([...familyAssetIds]),
            metricIds: diagnosticMetricIds,
          }
        : {
            status: "unavailable",
            reason: "insufficient_diagnostic_measures",
            family: null,
            assetIds: sortedUnique([...familyAssetIds]),
            metricIds: [],
          };
  }

  // The landing-page layout: declared by the recorded recipe, built on its
  // composites, and ranked over the assets those composites score.
  const lpCompositeMetricIds = uniqueInOrder(
    composites.map((composite) => composite.id),
  );
  const landingPageAssetIds = testedAssetIdsForTypes(
    testedAssets,
    composites.map((composite) => composite.appliesTo),
  );
  let landingPage: LiveLandingPageExhibitsCapability;
  if (deckLayout !== "landing_page") {
    landingPage = {
      status: "unavailable",
      reason: "no_declared_layout",
      assetIds: landingPageAssetIds,
      compositeMetricIds: [],
      comprehensionSourceIds: [],
    };
  } else if (lpCompositeMetricIds.length === 0) {
    landingPage = {
      status: "unavailable",
      reason: "no_composite_metrics",
      assetIds: landingPageAssetIds,
      compositeMetricIds: [],
      comprehensionSourceIds: [],
    };
  } else if (landingPageAssetIds.length < 2) {
    landingPage = {
      status: "unavailable",
      reason: "insufficient_items",
      assetIds: landingPageAssetIds,
      compositeMetricIds: [],
      comprehensionSourceIds: [],
    };
  } else {
    // Composite first, raw metrics second, exactly as
    // `asset_scores_for_topic` resolves the topic at build time. A study whose
    // questions carry registered `lp:` tags is read by its tags only; the id
    // readers below are the legacy path for an untagged saved study.
    const typedTopics = declaresLpTopics(questions);
    const questionIdForMetric = (metricId: string): string | undefined =>
      (metricPlan?.metrics ?? []).find((metric) => metric.id === metricId)
        ?.questionId ??
      (metricPlan?.qualitativeAnalyses ?? []).find(
        (analysis) =>
          analysis.classifier != null &&
          openEndClassifierMetricId(
            analysis.stableKey,
            analysis.classifier.metricKind,
          ) === metricId,
      )?.questionId;
    const metricTopic = (metricId: string): LpComposite | null => {
      const questionId = questionIdForMetric(metricId);
      return lpTopicOfTags(
        questionId ? questionsById.get(questionId)?.tags : undefined,
      );
    };
    const compositeTopic = (compositeId: string): LpComposite | null => {
      const composite = composites.find(
        (candidate) => candidate.id === compositeId,
      );
      const topics = new Set(
        componentsOf(composite)
          .map((component) => metricTopic(component.metricId))
          .filter((topic): topic is LpComposite => topic !== null),
      );
      return topics.size === 1 ? [...topics][0] : null;
    };
    const comprehensionComposites = typedTopics
      ? lpCompositeMetricIds.filter(
          (id) => compositeTopic(id) === LP_COMPREHENSION_TOPIC,
        )
      : lpCompositeMetricIds.filter((id) =>
          id.toLowerCase().includes(LEGACY_COMPREHENSION_SUBSTRING),
        );
    const comprehensionMetrics = uniqueInOrder(
      metrics
        .map((metric) => metric.id)
        .filter((id) =>
          typedTopics
            ? metricTopic(id) === LP_COMPREHENSION_TOPIC
            : legacyMetricTopicKey(id).includes(
                LEGACY_COMPREHENSION_SUBSTRING,
              ),
        ),
    );
    landingPage = {
      status: "available",
      reason: "available",
      assetIds: landingPageAssetIds,
      compositeMetricIds: lpCompositeMetricIds,
      comprehensionSourceIds:
        comprehensionComposites.length > 0
          ? comprehensionComposites
          : comprehensionMetrics,
    };
  }

  // The resolved binding is the gate. `plan.priceCurve` is what
  // `projectReportingPlan` rebound and kept, or null; a role question that was
  // deleted, retyped or replaced by a different type has already emptied it
  // there, so nothing is re-checked here.
  const priceCurveShape = REPORT_CARD_SHAPE_CAPABILITIES.price_curve;
  const priceRoleQuestionIds = uniqueInOrder(
    Object.keys(PRICE_CURVE_ROLES).flatMap((role) => {
      const questionId = plan?.priceCurve?.questionIdByRole?.[role];
      return typeof questionId === "string" && questionId.length > 0
        ? [questionId]
        : [];
    }),
  );
  const priceRoleQuestions = priceRoleQuestionIds.flatMap((questionId) => {
    const question = questionsById.get(questionId);
    return question && sourceState.reportQuestionIds.has(questionId)
      ? [question]
      : [];
  });
  const priceCurveMetricIds = uniqueInOrder(
    priceRoleQuestions.flatMap((question) =>
      metrics
        .filter((metric) => metric.questionId === question.id)
        .map((metric) => metric.id),
    ),
  );
  let priceCurve: LivePriceCurveCapability;
  if (plan?.priceCurve == null || priceRoleQuestions.length === 0) {
    priceCurve = {
      status: "unavailable",
      reason: "binding_unresolved",
      questionIds: [],
      metricIds: [],
    };
  } else if (
    !priceRoleQuestions.every((question) =>
      (
        priceCurveShape.sourceQuestionTypes as ReadonlyArray<string>
      ).includes(question.type),
    )
  ) {
    priceCurve = {
      status: "unavailable",
      reason: "unsupported_source_question_type",
      questionIds: priceRoleQuestions.map((question) => question.id),
      metricIds: [],
    };
  } else if (
    priceRoleQuestions.length < priceCurveShape.sourceQuestionCardinality.min ||
    (priceCurveShape.sourceQuestionCardinality.max !== null &&
      priceRoleQuestions.length >
        priceCurveShape.sourceQuestionCardinality.max)
  ) {
    priceCurve = {
      status: "unavailable",
      reason: "source_question_cardinality",
      questionIds: priceRoleQuestions.map((question) => question.id),
      metricIds: [],
    };
  } else {
    priceCurve = {
      status: "available",
      reason: "available",
      questionIds: priceRoleQuestions.map((question) => question.id),
      metricIds: priceCurveMetricIds,
    };
  }

  return {
    reportQuestionIds: sortedUnique(sourceState.reportQuestionIds),
    quantitative: live.quantitative,
    qualitative: live.qualitative,
    untargetedCustomQuantitative: untargeted.quantitative,
    untargetedCustomQualitative: untargeted.qualitative,
    quadrant,
    podium,
    scorecard,
    landingPage,
    priceCurve,
  };
}

type ReportBindingOutcome = NonNullable<ReportBindingOutcomes["comparison"]>;

/** A declared binding, resolved: its typed outcome and, when bound, its read. */
interface ResolvedBinding<T> {
  outcome: ReportBindingOutcome;
  binding: T | null;
}

/** The grid types a comparison table draws (one row per subject): every grid. */
const COMPARISON_SOURCE_QUESTION_TYPES: ReadonlySet<string> = MATRIX_QUESTION_TYPES;

/**
 * The questions a module's live source bindings name, in questionnaire order.
 * `withCustomRoutes` adds custom questions routed into the module as a report
 * input (a comparison table takes one; the map, lift and curve read recipe
 * questions only).
 */
type ModuleFeeders = (
  moduleId: string,
  withCustomRoutes: boolean,
) => QuestionDefinition[];

/**
 * No feeder now. `no_source` when the binding had one (any recorded outcome
 * but `not_planned`), so a grid the user deleted is told apart from a read
 * the study never planned; the recorded outcome is the draft's own, so the
 * projection stays a pure, idempotent function of the draft.
 */
const unfedOutcome = (
  previous: ReportBindingOutcome | null | undefined,
): ReportBindingOutcome =>
  previous == null || previous === "not_planned" ? "not_planned" : "no_source";

const measureIdsOf = (question: QuestionDefinition): string[] =>
  records((question.response as unknown as RecordValue).measures)
    .map((measure) => measure.id)
    .filter((id): id is string => typeof id === "string");

function resolveComparison(
  declared: DeclaredComparisonTable,
  previous: ReportBindingOutcome | null | undefined,
  feeders: ModuleFeeders,
): ResolvedBinding<ReportComparisonTable> {
  const fed = feeders(declared.moduleId, true);
  if (fed.length === 0) return { outcome: unfedOutcome(previous), binding: null };
  if (fed.length > 1) return { outcome: "ambiguous", binding: null };
  if (!COMPARISON_SOURCE_QUESTION_TYPES.has(fed[0].type)) {
    return { outcome: "method_unmet", binding: null };
  }
  return {
    outcome: "bound",
    binding: { questionId: fed[0].id, maxMeasures: declared.maxMeasures },
  };
}

function resolveAxis(
  declared: DeclaredReportAxis,
  feeders: ModuleFeeders,
): { outcome: "fed" | "unfed" | "ambiguous" | "method_unmet"; axis: ReportQuadrantAxes["x"] | null } {
  const fed = feeders(declared.moduleId, false);
  if (fed.length === 0) return { outcome: "unfed", axis: null };
  if (declared.measureRole == null) {
    if (fed.length > 1) return { outcome: "ambiguous", axis: null };
    return { outcome: "fed", axis: { questionId: fed[0].id } };
  }
  // The column is the one whose stamped role tag states the declared role,
  // in whatever language or wording it is labelled. Only a feeder with such
  // a column counts: another grid on the module (price perception beside the
  // equity grid) fills no axis, so it never makes the map ambiguous.
  const role = declared.measureRole;
  const columns = fed.flatMap((question) =>
    measureIdsFillingRole(question.tags ?? [], measureIdsOf(question), role).map(
      (measureId) => ({ questionId: question.id, measureId }),
    ),
  );
  if (columns.length === 0) return { outcome: "method_unmet", axis: null };
  if (columns.length > 1) return { outcome: "ambiguous", axis: null };
  return { outcome: "fed", axis: columns[0] };
}

function resolveQuadrantAxes(
  declared: DeclaredQuadrantAxes,
  previous: ReportBindingOutcome | null | undefined,
  feeders: ModuleFeeders,
): ResolvedBinding<ReportQuadrantAxes> {
  const x = resolveAxis(declared.x, feeders);
  const y = resolveAxis(declared.y, feeders);
  const outcomes = [x.outcome, y.outcome];
  if (outcomes.includes("ambiguous")) return { outcome: "ambiguous", binding: null };
  if (outcomes.includes("unfed")) return { outcome: unfedOutcome(previous), binding: null };
  if (
    outcomes.includes("method_unmet") ||
    !x.axis ||
    !y.axis ||
    (x.axis.questionId === y.axis.questionId && x.axis.measureId === y.axis.measureId)
  ) {
    return { outcome: "method_unmet", binding: null };
  }
  return {
    outcome: "bound",
    binding: {
      x: x.axis,
      y: y.axis,
      ...(declared.cornerLabels ? { cornerLabels: declared.cornerLabels } : {}),
    },
  };
}

function resolveLiftBaseline(
  declared: DeclaredLiftBaseline,
  previous: ReportBindingOutcome | null | undefined,
  feeders: ModuleFeeders,
): ResolvedBinding<ReportLiftBaseline> {
  const baseline = feeders(declared.baselineModuleId, false);
  const against = feeders(declared.againstModuleId, false);
  if (baseline.length > 1 || against.length > 1) {
    return { outcome: "ambiguous", binding: null };
  }
  if (baseline.length === 0 || against.length === 0) {
    return { outcome: unfedOutcome(previous), binding: null };
  }
  if (baseline[0].id === against[0].id) return { outcome: "method_unmet", binding: null };
  return {
    outcome: "bound",
    binding: { baselineQuestionId: baseline[0].id, againstQuestionId: against[0].id },
  };
}

/**
 * The curve's questions by the price role each one's plan attribution
 * declares (`planAttribution.priceRole`), never by position. All or nothing:
 * every role of the method claimed exactly once by a numeric read, and no
 * other read on the module.
 */
function resolvePriceCurve(
  declared: DeclaredPriceCurve,
  previous: ReportBindingOutcome | null | undefined,
  feeders: ModuleFeeders,
): ResolvedBinding<ReportPriceCurve> {
  const fed = feeders(declared.moduleId, false);
  if (fed.length === 0) return { outcome: unfedOutcome(previous), binding: null };
  const claims = declared.roles.map((role) =>
    fed.filter((question) => question.planAttribution?.priceRole === role),
  );
  if (claims.some((claimed) => claimed.length > 1)) {
    return { outcome: "ambiguous", binding: null };
  }
  const roster = Object.keys(PRICE_CURVE_ROLES);
  if (
    declared.roles.length !== roster.length ||
    roster.some((role) => !declared.roles.includes(role as PriceRole)) ||
    fed.length !== declared.roles.length ||
    new Set(fed.map((question) => question.id)).size !== fed.length ||
    claims.some((claimed) => claimed.length !== 1) ||
    fed.some((question) => question.type !== "continuous")
  ) {
    return { outcome: "method_unmet", binding: null };
  }
  // D8. The reference read is a courtesy marker: exactly one numeric read on
  // its module, else no marker and nothing else lost.
  const reference = declared.referenceModuleId
    ? feeders(declared.referenceModuleId, false)
    : [];
  return {
    outcome: "bound",
    binding: {
      questionIdByRole: Object.fromEntries(
        declared.roles.map((role, index) => [role, claims[index][0].id]),
      ),
      ...(reference.length === 1 && reference[0].type === "continuous"
        ? { referenceQuestionId: reference[0].id }
        : {}),
    },
  };
}

/**
 * Re-resolve a frozen report declaration against the final typed questions.
 *
 * The projection is clock-free and side-effect-free. It reads exact ids,
 * stable keys, question types, module ids and source origins only. It does not
 * import recipes, inspect wording, or assign new report meaning. A stamp other
 * than `reporting-projection.v2` is not a current plan, so the result is absent.
 */
export function projectReportingPlan(args: {
  plan: ReportingPlan | null | undefined;
  questions: ReadonlyArray<QuestionDefinition>;
  metricPlan?: MetricPlan | null;
  testedAssets?: TestedAssetBlock | null;
}): ReportingPlan | undefined {
  const { metricPlan, testedAssets, questions } = args;
  if (!args.plan) return undefined;
  // A retired stamp is not a plan a draft stores. The declarations are not
  // invented from it. A plan with no stamp, and a v2 plan that cannot be
  // projected, are returned as given.
  if (isRetiredReportingProjection(args.plan)) return undefined;
  if (!isProjectableReportingPlan(args.plan)) return args.plan;
  const plan = args.plan;

  const byStableKey = new Map(
    questions.map((question) => [question.stableKey, question]),
  );
  const activeQuestionIds = new Set<string>();
  // The modules each active question feeds through its live source binding:
  // as a recipe question, or as a custom question routed into a report input.
  const recipeModulesByQuestionId = new Map<string, Set<string>>();
  const routedModulesByQuestionId = new Map<string, Set<string>>();
  const feed = (
    byQuestion: Map<string, Set<string>>,
    questionId: string,
    moduleId: string,
  ) => byQuestion.set(questionId, (byQuestion.get(questionId) ?? new Set()).add(moduleId));
  const activeModules = new Set<string>();
  const activeCustomQuestionIds = new Set<string>();
  const customQuestionIdsByReportInput = new Map<string, string[]>();
  for (const binding of plan.sourceBindings ?? []) {
    const question = byStableKey.get(binding.stableKey);
    if (
      !question ||
      !bindingMatchesQuestion(
        binding as unknown as RecordValue,
        question as unknown as RecordValue,
      )
    ) {
      continue;
    }
    activeQuestionIds.add(question.id);
    const sourceRef = (binding as unknown as RecordValue).sourceRef;
    const reportInputModuleId = bindingReportInput(
      binding as unknown as RecordValue,
    );
    if (typeof reportInputModuleId === "string") {
      const isCustomSource =
        (validSourceRef(sourceRef) && sourceRef.kind === "custom_topic") ||
        binding.sourceKind === "reporting_target";
      if (isCustomSource) {
        activeCustomQuestionIds.add(question.id);
        customQuestionIdsByReportInput.set(reportInputModuleId, [
          ...(customQuestionIdsByReportInput.get(reportInputModuleId) ?? []),
          question.id,
        ]);
        feed(routedModulesByQuestionId, question.id, reportInputModuleId);
      } else {
        activeModules.add(reportInputModuleId);
        feed(recipeModulesByQuestionId, question.id, reportInputModuleId);
      }
    } else if (
      validSourceRef(sourceRef) && sourceRef.kind === "custom_topic"
    ) {
      activeCustomQuestionIds.add(question.id);
    }
  }
  // A frozen binding proves the input was planned. If its exact question is
  // rerouted, that is `unjoined`, never "not fielded". A retyped question
  // stays joined: its binding is its stableKey and sourceRef, and whether its
  // new type fits a card is the capability manifest's. Since
  // 2026-09-25 the operations applier removes a deleted question's binding,
  // so a later deletion reads `absent`; a draft that deleted a question
  // before then still carries the binding and reads `unjoined` (no backfill).
  const plannedModules = new Set(
    (plan.sourceBindings ?? [])
      .map((binding) => bindingReportInput(binding as unknown as RecordValue))
      .filter(
        (value): value is string =>
          typeof value === "string" && value.length > 0,
      ),
  );
  const activeById = new Map(
    questions
      .filter((question) => activeQuestionIds.has(question.id))
      .map((question) => [question.id, question]),
  );
  const feeders: ModuleFeeders = (moduleId, withCustomRoutes) =>
    questions.filter(
      (question) =>
        recipeModulesByQuestionId.get(question.id)?.has(moduleId) ||
        (withCustomRoutes &&
          routedModulesByQuestionId.get(question.id)?.has(moduleId)),
    );

  // DESIGN-REBINDING. The spec declares what each binding reads, by module
  // and typed role, never by question id; each is resolved here from the
  // draft as it is now, so a grid a revision replaces on the same module
  // rebinds.
  const declared: ReportingDeclaredBindings = (
    plan.projectionSpec as ReportingProjectionSpecV2
  ).bindings;
  const previousOutcomes: ReportBindingOutcomes = plan.bindingOutcomes ?? {};
  const resolutions = {
    quadrantAxes: declared.quadrantAxes
      ? resolveQuadrantAxes(declared.quadrantAxes, previousOutcomes.quadrantAxes, feeders)
      : null,
    liftBaseline: declared.liftBaseline
      ? resolveLiftBaseline(declared.liftBaseline, previousOutcomes.liftBaseline, feeders)
      : null,
    comparison: declared.comparison
      ? resolveComparison(declared.comparison, previousOutcomes.comparison, feeders)
      : null,
    priceCurve: declared.priceCurve
      ? resolvePriceCurve(declared.priceCurve, previousOutcomes.priceCurve, feeders)
      : null,
  };
  const quadrantAxes = resolutions.quadrantAxes?.binding ?? undefined;
  const liftBaseline = resolutions.liftBaseline?.binding ?? undefined;
  const comparison = resolutions.comparison?.binding ?? undefined;
  const priceCurve = resolutions.priceCurve?.binding ?? undefined;
  const bindingOutcomes: ReportBindingOutcomes = Object.fromEntries(
    Object.entries(resolutions).flatMap(([name, resolved]) =>
      resolved ? [[name, resolved.outcome]] : [],
    ),
  );
  const projectionSpec: ReportingProjectionSpecV2 = {
    schemaVersion: REPORTING_PROJECTION_V2,
    bindings: declared,
  };

  const resolvedBindings: Record<ReportProjectionBindingName, unknown> = {
    quadrantAxes,
    liftBaseline,
    comparison,
    priceCurve,
  };
  const capabilityPlan = {
    ...plan,
    projectionSpec,
    quadrantAxes,
    liftBaseline,
    comparison,
    priceCurve,
  };
  const capabilities = deriveLiveReportCapabilities({
    plan: capabilityPlan,
    questions,
    metricPlan,
    testedAssets,
  });
  const requirementMet = (requirement: ReportProjectionRequirement): boolean => {
    if (requirement.kind === "sources") return true;
    // A declared axis pair is only a reportable quadrant when the live typed
    // projection confirms it has two comparable items. A pair of references
    // alone can still resolve for a one-item study, but it cannot draw this
    // card and must remain an unavailable candidate rather than reach the
    // accepted-state validator.
    if (requirement.binding === "quadrantAxes") {
      return capabilities.quadrant.status === "available";
    }
    if (requirement.binding === "priceCurve") {
      return capabilities.priceCurve.status === "available";
    }
    return resolvedBindings[requirement.binding] != null;
  };
  // An exact custom source is rendered in question order downstream. Preserve
  // that declared journey order here; sorting opaque question ids turns an
  // implementation detail into a research narrative.
  const customMetricQuestionIds = new Set(
    (metricPlan?.metrics ?? []).map((metric) => metric.questionId),
  );
  const customQualitativeAnalysisQuestionIds = new Set(
    (metricPlan?.qualitativeAnalyses ?? []).map((analysis) => analysis.questionId),
  );
  const customQuantitativeQuestionIds = questions
    .filter(
      (question) =>
        activeCustomQuestionIds.has(question.id) &&
        customMetricQuestionIds.has(question.id),
    )
    .map((question) => question.id);
  const customQualitativeQuestionIds = questions
    .filter(
      (question) =>
        activeCustomQuestionIds.has(question.id) &&
        customQualitativeAnalysisQuestionIds.has(question.id),
    )
    .map((question) => question.id);
  const questionIdsForMetricIds = (
    metricIds: ReadonlyArray<string>,
  ): string[] => {
    const wanted = new Set(metricIds);
    return sortedUnique(
      (metricPlan?.metrics ?? [])
        .filter((metric) => wanted.has(metric.id))
        .map((metric) => metric.questionId),
    );
  };
  const availableSource = (
    questionIds: ReadonlyArray<string>,
  ): { status: "available"; questionIds: string[] } | null =>
    questionIds.length > 0
      ? { status: "available", questionIds: sortedUnique(questionIds) }
      : null;
  // The podium's sources: the study-verdict slot ranks the study's primary
  // question (`plan:primary`), the only podium composite.
  const podiumSourceQuestionIds = (): string[] =>
    capabilities.podium.status === "available"
      ? questions
          .filter((question) => question.tags.includes("plan:primary"))
          .map((question) => question.id)
      : [];
  // The one gate for a podium that ranks what was chosen: it exists only when
  // the study-verdict capability is available (which embeds
  // `primaryRanksTestedItems`), and it ranks the primary. Unavailable is
  // `absent`, never `unjoined`, so Featured still falls back. Other options,
  // and the other podium bases, return null and resolve as before.
  const chosenPodiumAvailability = (
    option: Pick<ReportOptionPlan, "shape" | "basis">,
    modules: ReadonlyArray<string>,
  ): {
    status: "planned" | "absent";
    reason: undefined;
    sourceQuestionIds: string[] | undefined;
    moduleIds: string[];
  } | null => {
    if (option.shape !== "podium" || option.basis !== "chosen") return null;
    const sourceQuestionIds = podiumSourceQuestionIds();
    return sourceQuestionIds.length > 0
      ? {
          status: "planned",
          reason: undefined,
          sourceQuestionIds,
          moduleIds: sortedUnique(
            modules.filter((moduleId) => activeModules.has(moduleId)),
          ),
        }
      : { status: "absent", reason: undefined, sourceQuestionIds: undefined, moduleIds: [] };
  };
  const routedCustomSource = (
    modules: ReadonlyArray<string>,
  ): { status: "available"; questionIds: string[] } | null =>
    availableSource(
      modules.flatMap(
        (moduleId) => customQuestionIdsByReportInput.get(moduleId) ?? [],
      ),
    );
  const customCapabilityForCard = (
    card: ReportingPlan["cards"][number]["card"],
    modules: ReadonlyArray<string>,
  ): { status: "available"; questionIds: string[] } | null => {
    const routed = routedCustomSource(modules);
    if (routed) return routed;
    if (card === "quotes") {
      return availableSource(customQualitativeQuestionIds);
    }
    // An arbitrary custom measure cannot inherit a recipe card's meaning. It
    // enters the summary through the generic exact-question options below.
    // Only an explicit typed report-input route can supply a semantic legacy
    // card such as diagnostics or KPI rings. A quotes card is already defined
    // by the surviving typed qualitative sources above.
    if (card === "quadrant" && capabilities.quadrant.status === "available") {
      return availableSource(
        questionIdsForMetricIds(capabilities.quadrant.metricIds),
      );
    }
    if (card === "podium") {
      return availableSource(podiumSourceQuestionIds());
    }
    return null;
  };
  const customCapabilityForOption = (
    option: Pick<ReportOptionPlan, "shape">,
    modules: ReadonlyArray<string> = [],
  ): { status: "available"; questionIds: string[] } | null => {
    // A custom podium is sourced only by the study-verdict capability: a
    // routed report input never makes a question a podium.
    const routed =
      option.shape === "podium" ? null : routedCustomSource(modules);
    if (routed) {
      const allowedTypes = new Set(
        REPORT_CARD_SHAPE_CAPABILITIES[option.shape].sourceQuestionTypes,
      );
      const compatible = availableSource(
        routed.questionIds.filter((questionId) => {
          const type = activeById.get(questionId)?.type;
          return type != null && allowedTypes.has(type);
        }),
      );
      if (compatible) return compatible;
    }
    if (
      option.shape === "quadrant" &&
      capabilities.quadrant.status === "available"
    ) {
      return availableSource(
        questionIdsForMetricIds(capabilities.quadrant.metricIds),
      );
    }
    if (option.shape === "podium") {
      return availableSource(podiumSourceQuestionIds());
    }
    return null;
  };
  const genericCustomCapabilityForOption = (
    option: Pick<ReportOptionPlan, "shape">,
    excludeQuestionIds: ReadonlySet<string> = new Set(),
  ): { status: "available"; questionIds: string[] } | null => {
    if (customQuantitativeQuestionIds.length === 0) return null;
    const directlyProjectable = new Set<ReportOptionPlan["shape"]>([
      "kpi_rows",
      "scorecard",
      "distribution",
      "question",
    ]);
    if (!directlyProjectable.has(option.shape)) return null;
    const allowedTypes = new Set(
      REPORT_CARD_SHAPE_CAPABILITIES[option.shape].sourceQuestionTypes,
    );
    return availableSource(
      customQuantitativeQuestionIds.filter((questionId) => {
        if (excludeQuestionIds.has(questionId)) return false;
        const question = activeById.get(questionId);
        if (!question || isSurveyDataOnlyQuestion(question)) return false;
        return allowedTypes.has(question.type);
      }),
    );
  };
  const resolveAvailability = (
    requirement: ReportProjectionRequirement,
    modules: ReadonlyArray<string>,
    whenAbsent: string,
    customCapability: { status: "available"; questionIds: string[] } | null,
  ): {
    status: "planned" | "absent" | "unjoined";
    reason: string | undefined;
    sourceQuestionIds: string[] | undefined;
    moduleIds: string[];
  } => {
    const moduleIds = sortedUnique(
      modules.filter((moduleId) => activeModules.has(moduleId)),
    );
    // The one owner of a declared binding's card status: its typed outcome.
    // Two feeders are a fault the user can fix (`unjoined`, its reason). A
    // read that was not planned, is gone (`no_source`: the user was told at
    // Save, owner 2026-10-19) or cannot carry the method is the recipe's own
    // absence, so another chart leads; a bound binding draws only where the
    // live capability confirms it.
    if (requirement.kind === "declared_binding") {
      const outcome = bindingOutcomes[requirement.binding];
      if (outcome === "ambiguous") {
        return {
          status: "unjoined",
          reason: AMBIGUOUS_BINDING_SENTENCE,
          sourceQuestionIds: undefined,
          moduleIds,
        };
      }
      if (outcome === "bound" && requirementMet(requirement)) {
        if (moduleIds.length > 0) {
          return { status: "planned", reason: undefined, sourceQuestionIds: undefined, moduleIds };
        }
        if (customCapability) {
          return {
            status: "planned",
            reason: undefined,
            sourceQuestionIds: customCapability.questionIds,
            moduleIds: [],
          };
        }
      }
      return {
        status: "absent",
        reason: whenAbsent,
        sourceQuestionIds: undefined,
        moduleIds: [],
      };
    }
    if (moduleIds.length > 0) {
      return {
        status: "planned",
        reason: undefined,
        sourceQuestionIds: undefined,
        moduleIds,
      };
    }
    if (customCapability) {
      return {
        status: "planned",
        reason: undefined,
        sourceQuestionIds: customCapability.questionIds,
        moduleIds: [],
      };
    }
    if (modules.some((moduleId) => plannedModules.has(moduleId))) {
      return {
        status: "unjoined",
        reason: UNJOINED_SENTENCE,
        sourceQuestionIds: undefined,
        moduleIds: [],
      };
    }
    return {
      status: "absent",
      reason: whenAbsent,
      sourceQuestionIds: undefined,
      moduleIds: [],
    };
  };

  const cards = plan.cards.map((card) => {
    const candidateModules = card.candidateModules ?? card.fromModules ?? [];
    const candidateWhenAbsent =
      card.candidateWhenAbsent ?? card.whenAbsent ?? "";
    const availability = resolveAvailability(
      card.requirement!,
      candidateModules,
      candidateWhenAbsent,
      customCapabilityForCard(card.card, candidateModules),
    );
    const hasQuestionLevelCustomQuantitativeFindings =
      card.card === "diagnostics" &&
      availability.status === "absent" &&
      customQuantitativeQuestionIds.length > 0;
    const projectedAbsenceReason = hasQuestionLevelCustomQuantitativeFindings
      ? "The recipe-owned diagnostics read was not fielded. Custom quantitative findings remain available in the question-level report."
      : availability.reason;
    return {
      ...card,
      candidateModules,
      candidateWhenAbsent,
      fromModules: availability.moduleIds,
      // Recipe absence copy describes recipe evidence. Keep it away from a
      // card currently powered by exact custom questions while preserving the
      // frozen sentence above for later re-projection.
      whenAbsent: availability.sourceQuestionIds
        ? undefined
        : hasQuestionLevelCustomQuantitativeFindings
          ? projectedAbsenceReason
          : candidateWhenAbsent,
      available: availability.status === "planned",
      absentReason: projectedAbsenceReason,
      sourceQuestionIds: availability.sourceQuestionIds,
    };
  });
  const projectOptions = (
    slotName: ReportCardSlotName,
    options: NonNullable<ReportingPlan["slots"]>["plot"]["options"],
    whenAbsent: string,
  ): ReportOptionPlan[] => {
    const resolved = options
      .filter(
        (option) => {
          const candidateModules =
            option.candidateModules ?? option.fromModules;
          return !(
            candidateModules.length === 0 &&
            (option.sourceQuestionIds?.length ?? 0) > 0
          );
        },
      )
      .map((option) => {
        const candidateModules =
          option.candidateModules ?? option.fromModules;
        const availability =
          chosenPodiumAvailability(option, candidateModules) ??
          resolveAvailability(
            option.requirement!,
            candidateModules,
            whenAbsent,
            customCapabilityForOption(option, candidateModules),
          );
        return {
          ...option,
          candidateModules,
          fromModules: availability.moduleIds,
          status: availability.status,
          // Slot-wide absence copy is shown only when the entire slot has no
          // planned option. An unused alternative has no buyer-facing failure.
          reason:
            availability.status === "unjoined" ? availability.reason : undefined,
          sourceQuestionIds: availability.sourceQuestionIds,
        };
      });
    const mismatches = resolved.map((option, optionIndex) =>
      reportCardOptionCapabilityMismatches({
        slotName,
        option,
        optionIndex,
        questions,
        testedAssets,
      }),
    );
    // Keep the frozen alternative so later typed edits can re-project it, but
    // do not let a present source's incompatible presentation shape veto the
    // questionnaire. A report card is an optional view over valid collected
    // evidence: if its typed source count, type, item set, asset identity or
    // media support is insufficient, mark only that presentation unavailable
    // and let the slot choose another option or its honest absence state.
    // Missing sources, malformed declarations and declared method
    // prerequisites never enter this branch and therefore remain fail-closed.
    return resolved.map((option, optionIndex) => {
      const optionMismatches = mismatches[optionIndex];
      const isOptionalPresentationMismatch =
        option.status === "planned" &&
        option.requirement?.kind === "sources" &&
        optionMismatches.length > 0 &&
        optionMismatches.every(({ kind }) => kind === "presentation");
      return isOptionalPresentationMismatch
        ? { ...option, status: "absent", reason: undefined }
        : option;
    });
  };
  const kpiCandidateWhenAbsent =
    plan.slots!.kpi.candidateWhenAbsent ??
    plan.slots!.kpi.whenAbsent ??
    "";
  const plotCandidateWhenAbsent =
    plan.slots!.plot.candidateWhenAbsent ??
    plan.slots!.plot.whenAbsent ??
    "";
  const quotesCandidateWhenAbsent =
    plan.slots!.quotes.candidateWhenAbsent ??
    plan.slots!.quotes.whenAbsent ??
    "";
  let kpiOptions: ReportOptionPlan[] = projectOptions(
    "kpi",
    plan.slots!.kpi.options ?? [],
    kpiCandidateWhenAbsent,
  );
  let plotOptions: ReportOptionPlan[] = projectOptions(
    "plot",
    plan.slots!.plot.options,
    plotCandidateWhenAbsent,
  );
  let quoteOptions: ReportOptionPlan[] = projectOptions(
    "quotes",
    plan.slots!.quotes.options,
    quotesCandidateWhenAbsent,
  );
  // -------------------------------------------------------------------------
  // Slot ORDER, and the evidence candidates behind each recipe chain.
  //
  // The list order IS the precedence. `_fill_plot` and `_fill_kpi` take the
  // first option that builds and nothing re-ranks them afterwards, so there is
  // no second ranking here either.
  //
  //   Featured (plot): recipe options -> evidence `podium(chosen)` -> custom
  //     `featured_question` -> evidence `dim_rank` -> evidence `distribution`.
  //   Insights (kpi): recipe options, with the union `scorecard` immediately
  //     ahead of its recipe twin -> custom `kpi` placement.
  //
  // Only a VERDICT candidate outranks the reader's own placement. The
  // explanatory reads sit BEHIND it on purpose: on a grids-only study
  // `dim_rank` would repeat the scorecard's means, while the custom featured
  // question draws the full per-item spread, which is the richer card.
  //
  // Every candidate is derived from TYPED QUESTION FACTS -- type, response
  // shape, asset refs, tags, disposition -- and never from an id, a title, a
  // stable key or a recipe identity, so this stays generic over any SurveyJS
  // survey.
  // -------------------------------------------------------------------------
  const activeQuestionsInOrder = questions.filter((question) =>
    activeQuestionIds.has(question.id),
  );
  const studyTestsAssets = (testedAssets?.assets ?? []).length > 0;
  /**
   * PERMISSION, which is checked before presentation.
   *
   * A question may be summarised only when it is active (its frozen binding
   * still matches it) and carries a report disposition of its own: a live
   * recipe identity, a report-target alias, or `customReporting.kind ==
   * "report"`. `survey_data_only` never qualifies, and a question carrying no
   * disposition at all was never promised to the report. A frozen binding
   * whose question is gone cannot reach `activeQuestionIds`, so it yields no
   * candidate and its `unjoined` sentence stands.
   */
  const carriesReportDisposition = (question: QuestionDefinition): boolean => {
    const attribution = question.planAttribution;
    if (!attribution) {
      return false;
    }
    if (attribution.surveyDataOnlyReason != null) {
      return false;
    }
    const custom = attribution.customReporting;
    if (custom?.kind === "survey_data_only") {
      return false;
    }
    return (
      custom?.kind === "report" ||
      (typeof attribution.recipeModuleId === "string" &&
        attribution.recipeModuleId.length > 0) ||
      (typeof attribution.reportingTargetModuleId === "string" &&
        attribution.reportingTargetModuleId.length > 0)
    );
  };
  /**
   * Reads that are never a Summary card, whatever they measure.
   *
   * The `profile:` family is the fact that survives persistence: the canonical
   * normalizer stamps every question `role: "research"`, and a screener is an
   * ordinary question wearing `profile:eligibility` (see `profiling.ts`). The
   * declared role is still read first so an artifact that does carry
   * `screening` is honoured.
   */
  const excludedFromEvidence = (question: QuestionDefinition): boolean =>
    question.role === "screening" ||
    (question.tags ?? []).some(
      (tag) =>
        typeof tag === "string" &&
        (tag.startsWith(PROFILE_TAG_PREFIX) ||
          // The pre-exposure half of a lift pair is never a Summary card of its
          // own: the movement is the read (one tag reader, `lift-tags.ts`).
          readLiftTag(tag)?.half === "pre"),
    );
  const evidencePermitted = (question: QuestionDefinition): boolean =>
    carriesReportDisposition(question) && !excludedFromEvidence(question);
  const registeredRatingUnit = new Set<string>(RATING_UNIT_IDS);
  const ratingUnitsRegistered = (question: QuestionDefinition): boolean => {
    const response = question.response;
    if (response.kind === "rating") {
      return registeredRatingUnit.has(response.unit);
    }
    if (response.kind === "matrix_rating") {
      return (
        response.measures.length > 0 &&
        response.measures.every((measure) =>
          registeredRatingUnit.has(measure.unit),
        )
      );
    }
    return false;
  };
  /**
   * Is this rating an evaluation OF the tested items?
   *
   * The declared roles (`asset_evaluation` / `asset_follow_up`) are read
   * first, then the structural facts that say the same thing and do survive
   * normalization: an asset binding, matrix rows drawn from the tested assets,
   * or an asset ref on the stimulus or the rows.
   */
  const evaluatesTestedAssets = (question: QuestionDefinition): boolean =>
    question.role === "asset_evaluation" ||
    question.role === "asset_follow_up" ||
    (question.assetBinding != null && question.assetBinding.mode !== "none") ||
    (question.response.kind === "matrix_rating" &&
      question.response.rows.source === "tested_assets") ||
    (question.assetRefs ?? []).some(
      (reference) =>
        reference.role === "stimulus" || reference.role === "matrix_row",
    );
  /**
   * A rating the Summary may state: an evaluation of the tested items, or --
   * in a study that tests none (brand, pain, claims) -- any research rating on
   * a registered scale.
   */
  const eligibleSummaryRating = (question: QuestionDefinition): boolean =>
    evidencePermitted(question) &&
    RATING_DESIGN_QUESTION_TYPES.has(question.type) &&
    ratingUnitsRegistered(question) &&
    (evaluatesTestedAssets(question) || !studyTestsAssets);
  /**
   * The questions an option already draws, so no read is claimed twice inside
   * one slot. Only a planned option draws anything; an `absent` or `unjoined`
   * alternative resolved to nothing.
   *
   * Cross-slot overlap is deliberately NOT excluded here: a rating can be a
   * scorecard row in Insights and the distribution in Featured, and python's
   * one-read-one-area hold-back is what decides that.
   */
  const claimedQuestionIds = (
    options: ReadonlyArray<ReportOptionPlan>,
  ): Set<string> =>
    new Set(
      options
        .filter((option) => option.status === "planned")
        .flatMap((option) =>
          optionSourceQuestions({ option, questions }).map(
            (question) => question.id,
          ),
        ),
    );
  /** An option's questions in the order a producer will read them. */
  const optionQuestionIdsInReadOrder = (
    option: ReportOptionPlan,
  ): string[] => {
    const explicit = option.sourceQuestionIds ?? [];
    if (explicit.length > 0) {
      return uniqueInOrder(explicit);
    }
    // Module order first, then question order inside a module, exactly as
    // `summary_cards._AssemblyContext.questions_for` resolves it.
    return uniqueInOrder(
      option.fromModules.flatMap((moduleId) =>
        questions
          .filter(
            (question) =>
              reportSourceId(question as unknown as RecordValue) === moduleId,
          )
          .map((question) => question.id),
      ),
    );
  };
  const derivedEvidenceOption = (args: {
    shape: ReportOptionPlan["shape"];
    questionIds: ReadonlyArray<string>;
    basis?: ReportOptionPlan["basis"];
  }): ReportOptionPlan => ({
    shape: args.shape,
    fromModules: [],
    requirement: { kind: "sources" },
    status: "planned",
    ...(args.basis ? { basis: args.basis } : {}),
    // Source order is row order downstream, so a derived option keeps the
    // order it was derived in rather than being re-sorted.
    sourceQuestionIds: uniqueInOrder(args.questionIds),
  });
  /**
   * PRESENTATION, checked second and fail-closed.
   *
   * A candidate the mechanical capability contract cannot draw is not offered
   * at all: it would otherwise become a validation error on the draft.
   */
  const admittedOption = (
    slotName: ReportCardSlotName,
    option: ReportOptionPlan,
  ): ReportOptionPlan | null =>
    reportCardOptionCapabilityMismatches({
      slotName,
      option,
      // The index only names the issue path, and a candidate that mismatches
      // is dropped rather than reported, so its final position is irrelevant
      // here.
      optionIndex: 0,
      questions,
      testedAssets,
    }).length === 0
      ? option
      : null;

  const customPlacementQuestion = (
    placement: "featured_question" | "kpi",
  ): QuestionDefinition | undefined =>
    activeQuestionsInOrder.find(
      (question) =>
        question.planAttribution?.customReporting?.kind === "report" &&
        question.planAttribution.customReporting.summaryPlacement ===
          placement,
    );

  // --- Insights ------------------------------------------------------------
  const kpiCustomQuestion = customPlacementQuestion("kpi");
  let customKpiOption: ReportOptionPlan | null = null;
  if (kpiCustomQuestion) {
    const shape: ReportOptionPlan["shape"] =
      RATING_DESIGN_QUESTION_TYPES.has(kpiCustomQuestion.type)
        ? "scorecard"
        : "kpi_rows";
    // Stage validation admits only whole-question KPI sources. Keep the
    // projector fail-closed as well: a malformed persisted placement does not
    // become an incompatible card merely because it carried the placement tag.
    customKpiOption = admittedOption(
      "kpi",
      derivedEvidenceOption({ shape, questionIds: [kpiCustomQuestion.id] }),
    );
  }
  const kpiClaimed = claimedQuestionIds(kpiOptions);
  if (kpiCustomQuestion) {
    kpiClaimed.add(kpiCustomQuestion.id);
  }
  const unclaimedRatings = activeQuestionsInOrder.filter(
    (question) =>
      !kpiClaimed.has(question.id) && eligibleSummaryRating(question),
  );
  if (unclaimedRatings.length > 0) {
    // Ratings JOIN the scorecard, they do not queue behind it: `_fill_kpi` is
    // first-wins, so a later option would never show beside a buildable recipe
    // scorecard. One union option, the recipe twin's rows first and the
    // unclaimed ratings after, placed immediately AHEAD of that twin -- it
    // replaces the twin rather than sitting beside it, so no row is drawn
    // twice.
    // The twin is the scorecard the KPI walk would actually BUILD, and
    // `_fill_kpi` skips a non-planned option outright -- so it is the first
    // PLANNED scorecard, not the first scorecard. Taking the first of any
    // status let a leading absent scorecard donate no rows: the union was then
    // built without the planned twin's reads, placed ahead of it, and won --
    // replacing a card that had rows with one missing them (m3). Latent today,
    // because no shipped chain declares two scorecards; the fallback below
    // keeps the position identical for every chain that declares one.
    const plannedTwinIndex = kpiOptions.findIndex(
      (option) => option.shape === "scorecard" && option.status === "planned",
    );
    const twinIndex =
      plannedTwinIndex >= 0
        ? plannedTwinIndex
        : kpiOptions.findIndex((option) => option.shape === "scorecard");
    const twin = twinIndex < 0 ? null : kpiOptions[twinIndex];
    const unionIndex = twinIndex < 0 ? kpiOptions.length : twinIndex;
    const unionScorecard = admittedOption(
      "kpi",
      derivedEvidenceOption({
        shape: "scorecard",
        questionIds: [
          ...(twin ? optionQuestionIdsInReadOrder(twin) : []),
          ...unclaimedRatings.map((question) => question.id),
        ],
      }),
    );
    if (unionScorecard) {
      kpiOptions = [
        ...kpiOptions.slice(0, unionIndex),
        unionScorecard,
        ...kpiOptions.slice(unionIndex),
      ];
    }
  }
  if (customKpiOption) {
    kpiOptions = [...kpiOptions, customKpiOption];
  }

  // --- Featured ------------------------------------------------------------
  const featuredCustomQuestion = customPlacementQuestion("featured_question");
  const plotClaimed = claimedQuestionIds(plotOptions);
  if (featuredCustomQuestion) {
    plotClaimed.add(featuredCustomQuestion.id);
  }
  const plotCandidates = activeQuestionsInOrder.filter(
    (question) =>
      !plotClaimed.has(question.id) && evidencePermitted(question),
  );
  // The Featured podium ranks the study's primary, through the same gate as a
  // declared `podium/chosen` option. A planned declared option already claims
  // the primary, so it is not derived twice; no other pick is ever a podium.
  const derivedPodium = chosenPodiumAvailability(
    { shape: "podium", basis: "chosen" },
    [],
  );
  const verdictQuestion =
    derivedPodium?.status === "planned"
      ? plotCandidates.find((question) =>
          (derivedPodium.sourceQuestionIds ?? []).includes(question.id),
        )
      : undefined;
  const rankGridQuestion = plotCandidates.find(
    (question) =>
      question !== verdictQuestion &&
      question.type === "matrix_rating" &&
      eligibleSummaryRating(question) &&
      questionItemIds(question, testedAssets).length >=
        REPORT_CARD_SHAPE_CAPABILITIES.dim_rank.minimumItemCardinality,
  );
  const leadRatingQuestion = plotCandidates.find(
    (question) =>
      question !== verdictQuestion &&
      question !== rankGridQuestion &&
      question.type === "rating" &&
      eligibleSummaryRating(question),
  );
  // The Featured tail, in precedence order: the verdict, then the reader's own
  // placement, then the two explanatory reads. The custom placement is the one
  // entry that does not go through the gate again -- it is the persisted
  // placement's own whole-question card, and the `question` shape draws every
  // typed question.
  const plotTail: Array<ReportOptionPlan | null> = [
    verdictQuestion
      ? admittedOption(
          "plot",
          derivedEvidenceOption({
            shape: "podium",
            questionIds: [verdictQuestion.id],
            basis: "chosen",
          }),
        )
      : null,
    featuredCustomQuestion
      ? derivedEvidenceOption({
          shape: "question",
          questionIds: [featuredCustomQuestion.id],
        })
      : null,
    rankGridQuestion
      ? admittedOption(
          "plot",
          derivedEvidenceOption({
            shape: "dim_rank",
            questionIds: [rankGridQuestion.id],
          }),
        )
      : null,
    leadRatingQuestion
      ? admittedOption(
          "plot",
          derivedEvidenceOption({
            shape: "distribution",
            questionIds: [leadRatingQuestion.id],
          }),
        )
      : null,
  ];
  plotOptions = [
    ...plotOptions,
    ...plotTail.filter((option): option is ReportOptionPlan => option != null),
  ];
  if (!plotOptions.some((option) => option.status === "planned")) {
    const customSources = questions.filter(
      (question) =>
        activeQuestionIds.has(question.id) &&
        question.planAttribution?.customReporting?.kind === "report" &&
        question.planAttribution.evidenceRequirementIds != null,
    );
    // The generic card represents an explicitly reported custom measurement.
    // Missing frozen bindings cannot enter activeQuestionIds. A declared
    // option that failed does not withhold it: when a promised Featured chart
    // cannot be drawn, Featured shows another read (owner, 2026-10-19, D8),
    // and the explicitly reported one comes before Python's list fallback.
    // Through the same presentation gate as every derived option (C16-#61).
    const generic =
      customSources.length > 0
        ? admittedOption(
            "plot",
            derivedEvidenceOption({
              shape: "question",
              questionIds: sortedUnique(customSources.map((question) => question.id)),
            }),
          )
        : null;
    if (generic) plotOptions = [generic, ...plotOptions];
  }
  const plannedKpiExists = kpiOptions.some(
    (option) => option.status === "planned",
  );
  const kpiClaimedAfterRecipe = claimedQuestionIds(kpiOptions);
  if (!plannedKpiExists && customQuantitativeQuestionIds.length > 0) {
    // Reuse the same source compatibility as declared options. Prefer the
    // supported KPI shape that covers the most reads; never invent an item
    // binding to turn a whole matrix into one scalar row.
    const compatible = (["kpi_rows", "scorecard"] as const)
      .flatMap((shape) => {
        const source = genericCustomCapabilityForOption({ shape });
        const option = source
          ? admittedOption(
              "kpi",
              derivedEvidenceOption({ shape, questionIds: sortedUnique(source.questionIds) }),
            )
          : null;
        return option ? [option] : [];
      })
      .sort(
        (left, right) =>
          (right.sourceQuestionIds?.length ?? 0) -
          (left.sourceQuestionIds?.length ?? 0),
      );
    if (compatible[0]) kpiOptions = [compatible[0], ...kpiOptions];
  } else if (plannedKpiExists) {
    // A module-owned choice KPI must not hide an explicitly reportable custom
    // scalar. Keep the recipe option's ownership and append a separate
    // exact-id `kpi_rows` candidate so reserved `_fill_kpi` can still read
    // the existing continuous median/base path.
    const scalarSource = genericCustomCapabilityForOption(
      { shape: "kpi_rows" },
      kpiClaimedAfterRecipe,
    );
    const scalarOptions = (scalarSource?.questionIds ?? [])
      .filter((questionId) => {
        const question = activeById.get(questionId);
        return (
          question != null &&
          question.type === "continuous" &&
          evidencePermitted(question)
        );
      })
      .map((questionId) =>
        admittedOption(
          "kpi",
          derivedEvidenceOption({
            shape: "kpi_rows",
            questionIds: [questionId],
          }),
        ),
      )
      .filter((option): option is ReportOptionPlan => option != null);
    if (scalarOptions.length > 0) {
      kpiOptions = [...kpiOptions, ...scalarOptions];
    }
  }
  if (
    !quoteOptions.some((option) => option.status === "planned") &&
    customQualitativeQuestionIds.length > 0
  ) {
    const generic = admittedOption(
      "quotes",
      derivedEvidenceOption({
        shape: "quotes",
        questionIds: sortedUnique(customQualitativeQuestionIds),
      }),
    );
    if (generic) quoteOptions = [generic, ...quoteOptions];
  }
  const chosenIndex = (options: typeof plotOptions): number | undefined => {
    const index = options.findIndex((option) => option.status === "planned");
    return index < 0 ? undefined : index;
  };
  const exactCustomOption = (option: ReportOptionPlan | undefined): boolean =>
    option?.status === "planned" &&
    option.fromModules.length === 0 &&
    (option.sourceQuestionIds?.length ?? 0) > 0;
  const slots: NonNullable<ReportingPlan["slots"]> = {
    kpi: {
      ...plan.slots!.kpi,
      options: kpiOptions,
      chosenIndex: undefined,
      candidateWhenAbsent: kpiCandidateWhenAbsent,
      whenAbsent: kpiOptions.some(exactCustomOption)
        ? undefined
        : kpiCandidateWhenAbsent,
    },
    plot: {
      ...plan.slots!.plot,
      options: plotOptions,
      chosenIndex: chosenIndex(plotOptions),
      candidateWhenAbsent: plotCandidateWhenAbsent,
      whenAbsent: exactCustomOption(
        plotOptions[chosenIndex(plotOptions) ?? -1],
      )
        ? undefined
        : plotCandidateWhenAbsent,
    },
    quotes: {
      ...plan.slots!.quotes,
      options: quoteOptions,
      chosenIndex: chosenIndex(quoteOptions),
      candidateWhenAbsent: quotesCandidateWhenAbsent,
      whenAbsent: exactCustomOption(
        quoteOptions[chosenIndex(quoteOptions) ?? -1],
      )
        ? undefined
        : quotesCandidateWhenAbsent,
    },
  };

  return {
    ...plan,
    projectionSpec,
    bindingOutcomes,
    cards,
    slots,
    quadrantAxes,
    liftBaseline,
    comparison,
    priceCurve,
  };
}

/** Validate frozen report bindings without consulting today's recipe registry. */
export function reportingCoherenceIssues(
  payload: unknown,
): QuestionnaireValidationIssue[] {
  if (!record(payload)) return [];
  const questions = records(payload.researchQuestions);
  const issues: QuestionnaireValidationIssue[] = [];
  questions.forEach((question, index) => {
    const attribution = record(question.planAttribution)
      ? question.planAttribution
      : null;
    if (attribution?.schemaVersion !== "question-plan-attribution.v2") {
      if (attribution?.schemaVersion !== "question-plan-attribution.v3") return;
      const recipe =
        typeof attribution.recipeModuleId === "string" &&
        attribution.recipeModuleId.trim().length > 0;
      const custom = attribution.customReporting;
      const validCustom =
        record(custom) &&
        ((custom.kind === "report" &&
          (custom.summaryPlacement === "featured_question" ||
            custom.summaryPlacement === "kpi" ||
            custom.summaryPlacement === null) &&
          (custom.reportInputModuleId == null ||
            (typeof custom.reportInputModuleId === "string" &&
              custom.reportInputModuleId.trim().length > 0))) ||
          (custom.kind === "survey_data_only" &&
            typeof custom.reason === "string" &&
            custom.reason.trim().length > 0));
      if (
        Number(recipe) + Number(validCustom) !== 1 ||
        attribution.reportingTargetModuleId != null ||
        attribution.surveyDataOnlyReason != null
      ) {
        issues.push({
          path: `researchQuestions[${index}].planAttribution`,
          code: "reporting_plan_stale",
          severity: issueSeverity("reporting_plan_stale"),
          message:
            worded("reporting_plan_stale", "This question does not have exactly one explicit report disposition. Regenerate the questionnaire or undo the incomplete attribution before review."),
        });
      }
      return;
    }
    const fields = [
      attribution.recipeModuleId,
      attribution.reportingTargetModuleId,
      attribution.surveyDataOnlyReason,
    ];
    const valid = fields.filter(
      (value) => typeof value === "string" && value.trim().length > 0,
    );
    const malformed = fields.some(
      (value) =>
        value != null &&
        (typeof value !== "string" || value.trim().length === 0),
    );
    if (malformed || valid.length !== 1) {
      issues.push({
        path: `researchQuestions[${index}].planAttribution`,
        code: "reporting_plan_stale",
        severity: issueSeverity("reporting_plan_stale"),
        message:
          worded("reporting_plan_stale", "This question does not have exactly one explicit report disposition. Regenerate the questionnaire or undo the incomplete attribution before review."),
      });
    }
  });
  if (!record(payload.reportingPlan)) return issues;
  const plan = payload.reportingPlan;
  const byKey = new Map(
    questions.map((question) => [question.stableKey, question]),
  );
  const byId = new Map(questions.map((question) => [question.id, question]));
  const sources = new Set(questions.map(reportSourceId).filter(Boolean));
  let priceCurvePromised = records(plan.cards).some(
    (card) => card.card === "price_curve" && card.available === true,
  );
  const stale = (path: string, detail: string) =>
    issues.push({
      path: path ? `reportingPlan.${path}` : "reportingPlan",
      code: "reporting_plan_stale",
      severity: issueSeverity("reporting_plan_stale"),
      message: worded("reporting_plan_stale", `${detail} Update the report plan by regenerating the questionnaire, or undo the source change before review.`),
    });

  // Current typed plans are a bijection, not merely a list of individually
  // valid bindings. Otherwise a boundary can drop one source and faithfully
  // re-project the already-silent omission forever.
  if (Array.isArray(plan.sourceBindings)) {
    questions.forEach((question, questionIndex) => {
      if (!questionSourceRef(question)) return;
      const matches = records(plan.sourceBindings).filter(
        (binding) =>
          binding.stableKey === question.stableKey &&
          bindingMatchesQuestion(binding, question),
      );
      if (matches.length !== 1) {
        stale(
          "sourceBindings",
          `Report-capable question researchQuestions[${questionIndex}] has ${matches.length} exact frozen source bindings; expected one.`,
        );
      }
    });
  }

  const projectable = isProjectableReportingPlan(plan);
  if (projectable) {
    issues.push(
      ...reportCardShapeCapabilityIssues({
        plan,
        questions: questions as unknown as QuestionDefinition[],
        testedAssets: record(payload.testedAssets)
          ? (payload.testedAssets as unknown as TestedAssetBlock)
          : undefined,
      }),
    );
    const projected = projectReportingPlan({
      plan,
      questions: questions as unknown as QuestionDefinition[],
      metricPlan: record(payload.metricPlan)
        ? (payload.metricPlan as unknown as MetricPlan)
        : undefined,
      testedAssets: record(payload.testedAssets)
        ? (payload.testedAssets as unknown as TestedAssetBlock)
        : undefined,
    });
    if (
      projected &&
      stableJson(reportingResolution(plan as unknown as ReportingPlan)) !==
        stableJson(reportingResolution(projected))
    ) {
      stale(
        "",
        "The report resolution does not match the questionnaire's frozen typed sources.",
      );
    }
  } else if (plan.projectionSpec != null) {
    // Presence of a projection stamp opts the plan into the current contract.
    // It must never fall through to legacy point checks: that would let a
    // partially written current plan survive unchanged and present stale report
    // choices as if they had been re-projected.
    stale(
      "",
      "The current report projection contract is incomplete or malformed.",
    );
  } else {
    // Historical accepted plans predate projectionSpec. They retain their
    // explicit compatibility path and are checked through their frozen source
    // and method references rather than reverse-engineered into today's shape.
    records(plan.sourceBindings).forEach((binding, index) => {
      const question = byKey.get(binding.stableKey);
      if (!question || !bindingMatchesQuestion(binding, question)) {
        stale(
          `sourceBindings[${index}]`,
          "A planned report source was removed or lost its attribution.",
        );
      }
    });
  }

  if (record(plan.slots)) {
    for (const [name, slot] of Object.entries(plan.slots)) {
      if (!record(slot)) continue;
      records(slot.options).forEach((option, index) => {
        if (option.shape === "price_curve" && option.status === "planned") {
          priceCurvePromised = true;
        }
        if (option.status === "planned") {
          const exactQuestionIds = Array.isArray(option.sourceQuestionIds)
            ? option.sourceQuestionIds.filter(
                (id): id is string => typeof id === "string" && id.length > 0,
              )
            : [];
          if (
            exactQuestionIds.length > 0 &&
            exactQuestionIds.some((id) => !byId.has(id))
          ) {
            stale(
              `slots.${name}.options[${index}]`,
              "A planned report option no longer has a question supplying its evidence.",
            );
          } else if (
            exactQuestionIds.length === 0 &&
            Array.isArray(option.fromModules) &&
            option.fromModules.length > 0 &&
            !option.fromModules.some((id) => sources.has(id))
          ) {
            stale(
              `slots.${name}.options[${index}]`,
              "A planned report option no longer has a question supplying its evidence.",
            );
          }
        }
      });
    }
  }
  const questionRef = (id: unknown, path: string, measureId?: unknown) => {
    const question = byId.get(id);
    const measures =
      question && record(question.response)
        ? records(question.response.measures)
        : [];
    if (
      !question ||
      (measureId != null &&
        !measures.some((measure) => measure.id === measureId))
    ) {
      stale(
        path,
        "A report reference no longer resolves to its question or measure.",
      );
    }
  };
  if (record(plan.quadrantAxes)) {
    for (const axis of ["x", "y"]) {
      const ref = plan.quadrantAxes[axis];
      if (record(ref))
        questionRef(ref.questionId, `quadrantAxes.${axis}`, ref.measureId);
    }
  }
  if (record(plan.liftBaseline)) {
    questionRef(
      plan.liftBaseline.baselineQuestionId,
      "liftBaseline.baselineQuestionId",
    );
    questionRef(
      plan.liftBaseline.againstQuestionId,
      "liftBaseline.againstQuestionId",
    );
  }
  if (record(plan.comparison)) {
    questionRef(plan.comparison.questionId, "comparison.questionId");
  }
  if (priceCurvePromised || plan.priceCurve != null) {
    const roles =
      record(plan.priceCurve) && record(plan.priceCurve.questionIdByRole)
        ? plan.priceCurve.questionIdByRole
        : undefined;
    const expected = Object.keys(PRICE_CURVE_ROLES);
    if (
      !roles ||
      Object.keys(roles).length !== expected.length ||
      !expected.every(
        (role) =>
          typeof roles[role] === "string" && (roles[role] as string).length > 0,
      ) ||
      new Set(Object.values(roles)).size !== expected.length
    ) {
      stale(
        "priceCurve.questionIdByRole",
        "A price curve requires four distinct questions bound to its exact threshold roles.",
      );
    } else {
      for (const [role, id] of Object.entries(roles)) {
        if (byId.get(id)?.type !== "continuous") {
          stale(
            `priceCurve.questionIdByRole.${role}`,
            "A price-curve threshold no longer resolves to a numeric amount question.",
          );
        }
      }
    }
  }
  return issues;
}
ruleImpl("reporting_plan_stale", reportingCoherenceIssues);
